import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { forwardRef, useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/pt-br';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ptBR from 'date-fns/locale/pt-BR';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './appointment.css';
import axiosInstance from "../../../services/AxiosInstance";
import Api from "../../../constants/Api";
import { errorAlert, errorObjectAlert, successAlert, successToast, warnAlert, warnAlertTwoChoices, warnAlertYesOrNo } from "../../components/alert/defaultAlert";
import { formatDateTime } from "../../../utils/DateUtils";
import { formatNumberWithTwoDecimals, formatPhoneNumber } from "../../../utils/FormatUtils";
import CustomerSelect from "../../components/Custom/Main/Customer/CustomerSelect";
import CustomerQuickCreateModal from "../../components/Custom/Main/Customer/CustomerQuickCreateModal";
import BranchSelect from "../../components/Custom/Main/Branch/BranchSelect";
import CustomSelect from "../../components/Forms/Element/CustomSelect";
import { useBranch } from "../../../context/BranchContext";
import { getAvailableSlots, getCompanyInfo, getEmployeePhotoUrl, getEmployeesByBranch, getServicesByBranch } from '../../../services/PublicBookingService';
import { formatDateToYYYYMMDD, toLocalISOString } from "../../../utils/DateUtils";
import AppointmentDetailsModal from '../../components/Custom/Appointment/AppointmentDetailsModal';
import {
    AppointmentDepositBadge,
    DEPOSIT_SITUATIONS,
    AppointmentDepositDraftSection,
    buildDepositDraft,
    registerDeposit,
    requiresDeposit,
    validateDepositDraft
} from '../../components/Custom/Appointment/AppointmentDeposit';
import ClubCrownIcon from '../../components/Icon/ClubCrownIcon';
import ClubLateIcon from '../../components/Icon/ClubLateIcon';
import PlanDetails from '../../components/Custom/Subscription/Plan/PlanDetails';
import DashboardSectionHeader from '../../components/Common/DashboardSectionHeader';
import PageTitle from '../../layouts/PageTitle';
import { SearchOperation } from '../../../utils/ApiUtils';
import { worksOnDate } from '../../../utils/WorkDayUtils';
import { specialDayAsWorkingHours, specialDayHoursLabel, specialDaysOn } from '../../../utils/SpecialDayUtils';
import { isOnVacationOn, vacationMessage, vacationReturnLabel } from '../../../utils/VacationUtils';

// Setup the localizer for react-big-calendar
const localizer = momentLocalizer(moment);

/**
 * Tempo assumido por serviço sem duração cadastrada (dado legado: o campo hoje é
 * obrigatório e nasce zerado). Espelha DEFAULT_SERVICE_DURATION_MINUTES do servidor: se os
 * dois divergirem, a janela mostrada na tela deixa de ser a janela gravada.
 */
const DEFAULT_SERVICE_DURATION_MINUTES = 0;

const APPOINTMENT_BLOCK_REASON_OPTIONS = [
    { value: 'Folga', label: 'Folga' },
    { value: 'Reunião', label: 'Reunião' },
    { value: 'Treinamento', label: 'Treinamento' },
    { value: 'Compromisso pessoal', label: 'Compromisso pessoal' },
    { value: 'Ausência', label: 'Ausência' },
    { value: 'Outro', label: 'Outro' }
];

/**
 * Escolhe a primeira duração realmente informada entre as candidatas.
 *
 * Existe porque `a || b` trata zero como ausência, e zero passou a ser um tempo válido:
 * um serviço que não reserva janela na agenda. Sem isso, todo atendimento de zero minuto
 * viraria 30 na hora de salvar.
 */
const resolveDuration = (...candidates) => {
    for (const candidate of candidates) {
        const value = Number(candidate);
        if (Number.isFinite(value) && value >= 0) {
            return value;
        }
    }

    return DEFAULT_SERVICE_DURATION_MINUTES;
};

// O react-datepicker nasce em inglês. Registrar aqui só habilita o idioma: ele passa a valer
// nos calendários que pedirem locale="pt-BR", sem mexer nos demais formulários do sistema.
registerLocale('pt-BR', ptBR);

/**
 * Acompanha uma media query e devolve se ela casa agora. Serve para os casos em que o
 * layout muda de comportamento, e não só de aparência, e portanto não dá para resolver
 * no CSS. Reavalia ao girar o aparelho ou redimensionar a janela.
 */
const useMediaQuery = (query) => {
    const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

    useEffect(() => {
        const mediaQuery = window.matchMedia(query);
        const handleChange = (event) => setMatches(event.matches);

        setMatches(mediaQuery.matches);
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [query]);

    return matches;
};

/**
 * Gatilho do seletor de data da barra do quadro. É só o ícone: o dia escolhido já aparece
 * por extenso ao lado, então repetir a data num campo de texto só ocuparia largura.
 * O react-datepicker precisa da ref para ancorar o calendário no elemento certo.
 */
const BoardDatePickerButton = forwardRef(({ onClick }, ref) => (
    <button
        type="button"
        ref={ref}
        onClick={onClick}
        className="collab-nav-btn collab-datepicker-btn"
        title="Escolher uma data"
        aria-label="Escolher uma data"
    >
        <i className="fas fa-calendar-day"></i>
    </button>
));

const getDisplayName = (person, fallback = '-') => {
    if (!person) return fallback;

    const fullName = person.fullName?.trim();
    if (fullName) return fullName;

    const composedName = [person.firstName, person.lastName]
        .filter(Boolean)
        .join(' ')
        .trim();

    return composedName || person.name || fallback;
};

const getServiceLabel = (service, fallback = '-') => {
    return service?.name || service?.description || fallback;
};

// Um agendamento pode ter vários serviços. Usa a lista (serviceNames) quando vier; senão, o serviço único.
const getServicesLabel = (appointment, fallback = '-') => {
    if (appointment?.serviceNames && appointment.serviceNames.length > 0) {
        return appointment.serviceNames.join(' + ');
    }
    return getServiceLabel(appointment?.service, fallback);
};

const getDurationInMinutes = (appointment) => {
    const duration = Number(appointment?.duration ?? appointment?.durationMinutes);
    if (Number.isFinite(duration) && duration > 0) {
        return duration;
    }

    if (appointment?.scheduledDate && appointment?.endTime) {
        const derivedDuration = moment(appointment.endTime).diff(moment(appointment.scheduledDate), 'minutes');
        if (derivedDuration > 0) {
            return derivedDuration;
        }
    }

    // Duração total enriquecida pelo calendário é a fonte correta para registros antigos
    // cujo campo duration ficou vazio, evitando o fallback indevido de 60 minutos.
    const servicesDuration = Number(appointment?.servicesDuration);
    if (Number.isFinite(servicesDuration) && servicesDuration > 0) {
        return servicesDuration;
    }

    const serviceDuration = Number(appointment?.service?.duration);
    return Number.isFinite(serviceDuration) && serviceDuration > 0 ? serviceDuration : 0;
};

const formatAppointmentDuration = (appointment) => {
    const duration = getDurationInMinutes(appointment);
    return duration ? `${duration} minutos` : '-';
};

const parseAppointmentPrice = (price) => {
    if (price == null || price === '') {
        return null;
    }

    if (typeof price === 'number') {
        return Number.isFinite(price) ? price : null;
    }

    const rawValue = String(price)
        .trim()
        .replace(/\s+/g, '')
        .replace(/^R\$/i, '');

    if (!rawValue) {
        return null;
    }

    let normalizedValue = rawValue;
    const hasComma = normalizedValue.includes(',');
    const hasDot = normalizedValue.includes('.');

    if (hasComma && hasDot) {
        normalizedValue = normalizedValue.lastIndexOf(',') > normalizedValue.lastIndexOf('.')
            ? normalizedValue.replace(/\./g, '').replace(',', '.')
            : normalizedValue.replace(/,/g, '');
    } else if (hasComma) {
        normalizedValue = normalizedValue.replace(',', '.');
    }

    const parsedValue = Number(normalizedValue);
    return Number.isFinite(parsedValue) ? parsedValue : null;
};

const getAppointmentPriceValue = (appointment) => {
    if (!appointment) {
        return null;
    }

    return parseAppointmentPrice(appointment.price ?? appointment.service?.price);
};

const formatAppointmentPrice = (price) => {
    const parsedPrice = parseAppointmentPrice(price);
    return parsedPrice != null ? `R$ ${formatNumberWithTwoDecimals(parsedPrice)}` : '-';
};

/**
 * Preço do card com o prefixo "A partir de" quando algum serviço da visita tem preço aberto.
 *
 * O valor exibido continua sendo o mesmo: a marca só diz que ele é ponto de partida, para a
 * recepção não repassar como fechado ao cliente que liga perguntando quanto vai custar.
 */
const formatAppointmentPriceLabel = (appointment) => {
    const price = formatAppointmentPrice(getAppointmentPriceValue(appointment));
    if (price === '-' || !appointment?.priceFrom) {
        return price;
    }
    return `A partir de ${price}`;
};

const generateUUID = () => {
    return `APP-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 7).toUpperCase()}`;
};

/** Aceita apenas HH:mm dentro de um dia real, para o encaixe não gerar data inválida. */
const isValidTimeValue = (value) => {
    const match = String(value || '').match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return false;

    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
};

const formatSlotTime = (slot) => {
    if (slot?.time) return slot.time;
    if (slot?.startTime?.includes('T')) {
        return slot.startTime.split('T')[1].substring(0, 5);
    }
    return '';
};

const statusConfig = {
    // Violeta, e não amarelo: o dourado ficou reservado para o selo de membro do Clube,
    // e as duas marcas juntas no mesmo cartão eram lidas como a mesma coisa.
    SCHEDULED: { label: 'Agendado', badge: 'scheduled', color: '#7048c4', icon: 'fa-regular fa-calendar' },
    CONFIRMED: { label: 'Confirmado', badge: 'info', color: '#17a2b8', icon: 'fa-solid fa-circle-check' },
    IN_PROGRESS: { label: 'Em andamento', badge: 'primary', color: '#007bff', icon: 'fa-solid fa-bolt' },
    COMPLETED: { label: 'Concluído', badge: 'secondary', color: '#9ca3af', icon: 'fa-solid fa-check-double' },
    CANCELED: { label: 'Cancelado', badge: 'warning', color: '#f59e0b', icon: 'fa-solid fa-ban' },
    NO_SHOW: { label: 'Não compareceu', badge: 'danger', color: '#dc3545', icon: 'fa-solid fa-user-xmark' }
};

const buildQuickOption = (value, label, extra = {}) => ({
    value,
    label,
    ...extra
});

// Avatar component: shows employee photo or initials fallback
const EmployeeAvatar = ({ employee, companyUuid }) => {
    const [imgError, setImgError] = useState(false);
    const employeeName = getDisplayName(employee, '?');
    const initials = employeeName
        .split(' ')
        .map(n => n[0])
        .slice(0, 2)
        .join('')
        .toUpperCase();

    if (!imgError && companyUuid && employee?.id) {
        return (
            <img
                src={getEmployeePhotoUrl(companyUuid, employee.id)}
                alt={employeeName}
                className="collab-avatar"
                onError={() => setImgError(true)}
            />
        );
    }
    return (
        <div className="collab-avatar collab-avatar-initials">
            {initials}
        </div>
    );
};

const calendarViewLabels = {
    month: 'Mes',
    week: 'Semana',
    day: 'Dia'
};

const canceledVisibilityOptions = [
    { value: false, label: 'Ocultar cancelados' },
    { value: true, label: 'Mostrar cancelados' }
];

/**
 * Indica se o colaborador aparece no quadro da agenda, pela flag "Presta serviço?" do
 * cadastro. Ausente conta como presta, o mesmo tratamento do backend.
 */
const isServiceProvider = (employee) => employee?.providesService !== false;

const COLLABORATOR_BOARD_INTERVAL_MINUTES = 10;
const COLLABORATOR_BOARD_SLOT_HEIGHT = 34;
const COLLABORATOR_BOARD_HEADER_HEIGHT = 76;
const DEFAULT_BOARD_START_MINUTES = 0;
const getAppointmentLayoutKey = (appointment) => appointment?.resource?.id ?? appointment?.id;
const DEFAULT_BOARD_END_MINUTES = 24 * 60;

// getDay() (0=Dom..6=Sáb) → nome do enum DayOfWeek usado no working-hours do backend.
const JS_DAY_TO_ENUM = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

// "HH:mm" ou "HH:mm:ss" → minutos desde a meia-noite. Null quando o horário não veio preenchido.
const toMinutesOfDay = (value) => {
    if (!value) {
        return null;
    }

    const [hours, minutes] = value.split(':').map(Number);
    if (!Number.isInteger(hours) || !Number.isInteger(minutes)) {
        return null;
    }

    return (hours * 60) + minutes;
};

// Arredonda para baixo/cima na hora cheia, para a grade começar e terminar em marcações redondas.
const floorToHour = (minutes) => Math.floor(minutes / 60) * 60;
const ceilToHour = (minutes) => Math.min(Math.ceil(minutes / 60) * 60, DEFAULT_BOARD_END_MINUTES);

// Minutos desde a meia-noite de uma data local.
const dateToMinutesOfDay = (date) => (date.getHours() * 60) + date.getMinutes();

// Os 7 dias da semana que contém a data informada (usado na visão semanal do calendário).
const getWeekDays = (date) => {
    const weekStart = moment(date).startOf('week');
    return Array.from({ length: 7 }, (_, index) => weekStart.clone().add(index, 'days').toDate());
};

/**
 * Atendimento concluído que ainda não teve a venda gerada.
 *
 * Não é um status próprio (no banco continua COMPLETED), é um recorte operacional: o que já
 * foi cobrado sai do radar da recepção, o que não foi ainda precisa de ação. Por isso a cor
 * vive fora do statusConfig, que espelha o enum.
 */
const PENDING_SALE_SURFACE = { background: '#0f766e', border: '#115e59', text: '#ffffff' };

const getStatusSurface = (status) => {
    switch (status) {
        case 'SCHEDULED':
            return { background: '#ece5fb', border: '#b49df0', text: '#4a2f9c' };
        case 'CONFIRMED':
            return { background: '#d9f7c8', border: '#83d16a', text: '#256c1f' };
        case 'IN_PROGRESS':
            return { background: '#d4ecff', border: '#63b3ff', text: '#14507f' };
        case 'COMPLETED':
            return { background: '#9ca3af', border: '#9ca3af', text: '#1f2937' };
        case 'CANCELED':
            return { background: '#fef3c7', border: '#f59e0b', text: '#92400e' };
        case 'NO_SHOW':
            return { background: '#ffd8d8', border: '#dc3545', text: '#842029' };
        default:
            return { background: '#eef3f7', border: '#bfd0de', text: '#41515e' };
    }
};

const AppointmentCalendarToolbar = ({ label, onNavigate, onView, view }) => (
    <div className="appointment-calendar-toolbar">
        <div className="appointment-calendar-toolbar__nav">
            <button type="button" className="appointment-calendar-toolbar__btn" onClick={() => onNavigate('PREV')}>
                <i className="fas fa-chevron-left"></i>
            </button>
            <button type="button" className="appointment-calendar-toolbar__btn is-today" onClick={() => onNavigate('TODAY')}>
                Hoje
            </button>
            <button type="button" className="appointment-calendar-toolbar__btn" onClick={() => onNavigate('NEXT')}>
                <i className="fas fa-chevron-right"></i>
            </button>
        </div>

        <div className="appointment-calendar-toolbar__label">
            <strong>{label}</strong>
            <span>Calendario operacional</span>
        </div>

        <div className="appointment-calendar-toolbar__views" role="tablist" aria-label="Tipos de visualizacao do calendario">
            {['week', 'day', 'month'].map((item) => (
                <button
                    key={item}
                    type="button"
                    className={`appointment-calendar-toolbar__view-btn ${view === item ? 'is-active' : ''}`}
                    onClick={() => onView(item)}
                >
                    {calendarViewLabels[item]}
                </button>
            ))}
        </div>
    </div>
);

const CalendarEventContent = ({ event }) => {
    const customerName = getDisplayName(event.resource?.customer, 'Cliente');
    const serviceName = getServicesLabel(event.resource, 'Servico');
    const clubMember = event.resource?.customer?.clubMember;
    const clubLate = event.resource?.customer?.clubLate;

    return (
        <div className="appointment-calendar-event">
            <div className="appointment-calendar-event__time">
                <i className="fa-regular fa-clock"></i>
                <span>{moment(event.start).format('HH:mm')} - {moment(event.end).format('HH:mm')}</span>
                {event.resource?.appointmentOrigin === 'PUBLIC' && (
                    <i className="fa-solid fa-mobile-screen-button" title="Agendamento pelo app do cliente"></i>
                )}
                {event.resource?.appointmentOrigin === 'AI' && (
                    <i className="fa-solid fa-robot" title="Agendamento feito pela IA"></i>
                )}
                {event.resource?.appointmentOrigin === 'RECEPTION' && (
                    <i className="fa-solid fa-desktop" title="Agendamento feito pela recepção"></i>
                )}
                {event.resource?.appointmentOrigin === 'COLLABORATOR_PORTAL' && (
                    <i className="fa-solid fa-user-tie" title="Agendamento feito pelo colaborador no portal"></i>
                )}
            </div>
            <div className="appointment-calendar-event__customer">
                <i className="fa-regular fa-user"></i>
                <span>
                    {customerName}
                    {event.resource?.firstVisit && (
                        <i className="fa-solid fa-star appointment-first-visit-icon ms-1"
                            title="Primeiro atendimento deste cliente" aria-label="Primeiro atendimento" />
                    )}
                    {event.resource?.customerBirthday && (
                        <i className="fa-solid fa-cake-candles appointment-customer-birthday-icon ms-1"
                            title="Aniversário do cliente" aria-label="Aniversário do cliente" />
                    )}
                    {clubMember && <ClubCrownIcon className="club-crown-badge ms-1" />}
                    {clubLate && <ClubLateIcon className="club-late-icon ms-1" />}
                </span>
            </div>
            {event.resource?.customer?.clubPlanName && (
                <div
                    className="appointment-calendar-event__club-plan"
                    title={`Plano do Clube: ${event.resource.customer.clubPlanName}`}
                >
                    <i className="fa-solid fa-gem"></i>
                    <span>{event.resource.customer.clubPlanName}</span>
                </div>
            )}
            {event.resource?.customer?.branch?.name && (
                <div className="appointment-calendar-event__branch-origin" title={`Unidade de origem do cliente: ${event.resource.customer.branch.name}`}>
                    <i className="fa-solid fa-location-dot"></i>
                    <span>{event.resource.customer.branch.name}</span>
                </div>
            )}
            <div className="appointment-calendar-event__service">
                <i className="fa fa-list-ul"></i>
                <span>{serviceName}</span>
            </div>
        </div>
    );
};

const StatusLegend = ({ items }) => (
    <details className="appointment-dashboard-legend">
        {/* O controle nativo permite recolher todas as legendas também pelo teclado. */}
        <summary className="appointment-dashboard-legend__toggle" aria-label="Expandir ou minimizar legendas" title="Expandir ou minimizar legendas">
            <span>Legendas</span>
            <span className="appointment-dashboard-legend__action">
                <i className="fa-solid fa-chevron-down" aria-hidden="true"></i>
            </span>
        </summary>
        <div className="appointment-dashboard-legend__content">
            <div className="appointment-dashboard-legend__group is-status">
                <span className="appointment-dashboard-legend__group-title">Status do atendimento</span>
                <div className="appointment-dashboard-legend__list" role="list">
                    {items.map((item) => (
                        <span key={item.key} className="appointment-dashboard-legend__item" title={item.label} role="listitem">
                            <span
                                className="appointment-dashboard-legend__swatch"
                                style={{
                                    background: item.surface.background,
                                    borderColor: item.surface.border,
                                    color: item.surface.text
                                }}
                            >
                                <i className={item.icon}></i>
                            </span>
                            <span className="appointment-dashboard-legend__label">{item.label}</span>
                        </span>
                    ))}
                </div>
            </div>

            <div className="appointment-dashboard-legend__group is-indicators">
                <span className="appointment-dashboard-legend__group-title">Indicadores</span>
                <div className="appointment-dashboard-legend__list" role="list">
                    <span className="appointment-dashboard-legend__item" title="Membro do Clube" role="listitem">
                        <span
                            className="appointment-dashboard-legend__swatch"
                            style={{
                                background: 'linear-gradient(135deg, #f7d070 0%, #fdf6cf 40%, #dfb244 70%, #c39627 100%)',
                                borderColor: '#c39627',
                                color: '#3b2501'
                            }}
                        >
                            <ClubCrownIcon className="" title="" />
                        </span>
                        <span className="appointment-dashboard-legend__label">Membro do Clube</span>
                    </span>
                    <span className="appointment-dashboard-legend__item" title="Primeiro atendimento válido deste cliente" role="listitem">
                        <span className="appointment-dashboard-legend__swatch"
                            style={{ background: '#fff7d6', borderColor: '#e2b93b', color: '#9a6b00' }}>
                            <i className="fa-solid fa-star"></i>
                        </span>
                        <span className="appointment-dashboard-legend__label">Primeiro atendimento</span>
                    </span>
                    <span className="appointment-dashboard-legend__item" title="Aniversário do cliente na data do atendimento" role="listitem">
                        <span className="appointment-dashboard-legend__swatch"
                            style={{ background: '#fff0f2', borderColor: '#fb7185', color: '#be123c' }}>
                            <i className="fa-solid fa-cake-candles"></i>
                        </span>
                        <span className="appointment-dashboard-legend__label">Aniversário do cliente</span>
                    </span>
                    <span className="appointment-dashboard-legend__item" title="Venda registrada" role="listitem">
                        <span
                            className="appointment-dashboard-legend__swatch"
                            style={{
                                background: '#e5e7eb',
                                borderColor: '#9ca3af',
                                color: '#374151'
                            }}
                        >
                            <i className="fa-solid fa-receipt"></i>
                        </span>
                        <span className="appointment-dashboard-legend__label">Venda registrada</span>
                    </span>
                    <span className="appointment-dashboard-legend__item" title="Agendamento encaixado no mesmo horário" role="listitem">
                        <span
                            className="appointment-dashboard-legend__swatch"
                            style={{ background: '#fff3e8', borderColor: '#fd7e14', color: '#c65f0b' }}
                        >
                            <i className="fa-solid fa-arrows-to-dot"></i>
                        </span>
                        <span className="appointment-dashboard-legend__label">Encaixe</span>
                    </span>
                    <span className="appointment-dashboard-legend__item" title="Serviços adicionais ultrapassam a duração reservada" role="listitem">
                        <span className="appointment-dashboard-legend__swatch"
                            style={{ background: '#f3f4f6', borderColor: '#6b7280', color: '#111827' }}>
                            <i className="fa-solid fa-triangle-exclamation"></i>
                        </span>
                        <span className="appointment-dashboard-legend__label">Exceção de duração</span>
                    </span>
                    <span className="appointment-dashboard-legend__item" title="Confirmado pelo cliente via WhatsApp" role="listitem">
                        <span className="appointment-dashboard-legend__swatch"
                            style={{ background: '#eaf7ee', borderColor: '#25d366', color: '#128c4a' }}>
                            <i className="fa-brands fa-whatsapp"></i>
                        </span>
                        <span className="appointment-dashboard-legend__label">Confirmado via WhatsApp</span>
                    </span>
                    <span className="appointment-dashboard-legend__item" title="Mensagem enviada; aguardando confirmação do cliente" role="listitem">
                        <span className="appointment-dashboard-legend__swatch"
                            style={{ background: '#fff7e6', borderColor: '#f59e0b', color: '#b45309' }}>
                            <i className="fa-regular fa-clock"></i>
                        </span>
                        <span className="appointment-dashboard-legend__label">Aguardando confirmação</span>
                    </span>
                    <span className="appointment-dashboard-legend__item" title="Cliente com mensalidade do Clube em atraso" role="listitem">
                        <span className="appointment-dashboard-legend__swatch"
                            style={{ background: '#dc2626', borderColor: '#991b1b', color: '#ffffff' }}>
                            <i className="fa-solid fa-circle-exclamation"></i>
                        </span>
                        <span className="appointment-dashboard-legend__label">Inadimplente</span>
                    </span>
                </div>
            </div>

            <div className="appointment-dashboard-legend__group is-origin">
                <span className="appointment-dashboard-legend__group-title">Reserva do horário</span>
                <div className="appointment-dashboard-legend__list" role="list">
                    {/* Vem do mesmo mapa que desenha o ícone na agenda, para a legenda nunca
                        divergir de cor, ícone ou nome. */}
                    {Object.entries(DEPOSIT_SITUATIONS).map(([situation, meta]) => (
                        <span key={situation} className="appointment-dashboard-legend__item" title={meta.hint} role="listitem">
                            <span className="appointment-dashboard-legend__swatch"
                                style={{ background: '#ffffff', borderColor: '#d0d5dd' }}>
                                <AppointmentDepositBadge situation={situation} />
                            </span>
                            <span className="appointment-dashboard-legend__label">{meta.label}</span>
                        </span>
                    ))}
                </div>
            </div>

            <div className="appointment-dashboard-legend__group is-origin">
                <span className="appointment-dashboard-legend__group-title">Origem</span>
                <div className="appointment-dashboard-legend__list" role="list">
                    <span className="appointment-dashboard-legend__item" title="Agendamento realizado pelo app do cliente" role="listitem">
                        <span className="appointment-dashboard-legend__swatch"
                            style={{ background: '#eff6ff', borderColor: '#93c5fd', color: '#2563eb' }}>
                            <i className="fa-solid fa-mobile-screen-button"></i>
                        </span>
                        <span className="appointment-dashboard-legend__label">App do cliente</span>
                    </span>
                    <span className="appointment-dashboard-legend__item" title="Agendamento realizado pela recepção" role="listitem">
                        <span className="appointment-dashboard-legend__swatch"
                            style={{ background: '#f1f5f9', borderColor: '#cbd5e1', color: '#475467' }}>
                            <i className="fa-solid fa-desktop"></i>
                        </span>
                        <span className="appointment-dashboard-legend__label">Recepção</span>
                    </span>
                    <span className="appointment-dashboard-legend__item" title="Agendamento feito pelo colaborador no portal" role="listitem">
                        <span className="appointment-dashboard-legend__swatch"
                            style={{ background: '#f5f3ff', borderColor: '#c4b5fd', color: '#7c3aed' }}>
                            <i className="fa-solid fa-user-tie"></i>
                        </span>
                        <span className="appointment-dashboard-legend__label">Portal do colaborador</span>
                    </span>
                </div>
            </div>

        </div>
    </details>
);

const StatusPill = ({ status, subtle = false }) => {
    const config = statusConfig[status] || {
        label: status,
        icon: 'fa-solid fa-circle',
        color: '#6c757d'
    };
    const surface = getStatusSurface(status);

    return (
        <span
            className={`appointment-status-pill ${subtle ? 'is-subtle' : ''}`}
            style={{
                '--status-pill-color': config.color,
                '--status-pill-background': surface.background,
                '--status-pill-border': surface.border,
                '--status-pill-text': surface.text
            }}
        >
            <i className={config.icon}></i>
            <span>{config.label}</span>
        </span>
    );
};

const AppointmentDashboard = ({ standalone = false }) => {
    const [searchParams] = useSearchParams();
    const [initialDate] = useState(() => {
        const date = moment(searchParams.get('date'), 'YYYY-MM-DD', true);
        return standalone && date.isValid() ? date.toDate() : new Date();
    });
    const initialCollaboratorRef = useRef(standalone ? searchParams.get('collaborator') : null);

    const { t, i18n } = useTranslation();
    const { selectedBranch } = useBranch();
    const [appointments, setAppointments] = useState([]);
    // Contador de releitura: qualquer ação que altera agendamento incrementa, e o efeito de
    // busca refaz a consulta com a data vigente. Ver refreshAppointments.
    const [appointmentsRefreshToken, setAppointmentsRefreshToken] = useState(0);
    // Expediente da filial ativa (um registro por dia da semana), usado para recortar a grade de horas.
    const [branchWorkingHours, setBranchWorkingHours] = useState([]);
    // Datas especiais das filiais exibidas (feriado fechado, horário especial), que valem por cima
    // do expediente semanal na grade e no aviso de dia fechado.
    const [specialDays, setSpecialDays] = useState([]);
    const [cardDragPreview, setCardDragPreview] = useState(null);
    const [showLoading, setShowLoading] = useState(false);
    const [currentDate, setCurrentDate] = useState(initialDate);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    
    // Identificador público da empresa, usado nas consultas do planner e nas fotos dos colaboradores.
    const [companyUuid, setCompanyUuid] = useState(null);

    // UseRef para preservar o estado do agendamento selecionado
    const selectedAppointmentRef = useRef(null);

    // Data para a qual o quadro já foi posicionado. Impede que um refresh de dados
    // reposicione a rolagem por baixo do operador.
    const boardScrollAnchorRef = useRef(null);
    // Última posição de rolagem conhecida. Durante o carregamento o quadro dá lugar ao
    // spinner, então o elemento é destruído e recriado zerado: sem guardar aqui, não há de
    // onde restaurar depois.
    const boardScrollMemoryRef = useRef({ top: 0, left: 0 });
    const collaboratorBoardShellRef = useRef(null);

    // Estado do arrasto para navegar pelo quadro. Fica em ref, e não em state, porque muda a
    // cada mousemove: em state, cada pixel arrastado re-renderizaria o quadro inteiro.
    const boardPanRef = useRef(null);

    // Marca que o gesto que acabou foi um arrasto, não um clique. Os dois handlers de clique
    // do quadro (abrir cartão e criar agendamento em espaço vazio) consultam isto: sem essa
    // trava, arrastar para o lado terminaria abrindo um agendamento no ponto onde o dedo parou.
    const boardDidPanRef = useRef(false);

    // Arrasto de cartão (remanejar horário/colaborador ou ajustar duração). O gesto em si fica
    // em ref, pelo mesmo motivo do pan; só a prévia vai para state, e ela muda no máximo uma
    // vez por faixa de 10 minutos cruzada.
    const cardDragRef = useRef(null);

    // Sequência da última busca de agendamentos disparada. Trocar de data dispara uma busca
    // nova sem cancelar a anterior, e a antiga pode responder depois: sem esse controle, a
    // lista do mês antigo sobrescreve a do mês novo e a agenda aparece vazia.
    const appointmentsRequestIdRef = useRef(0);

    // Alvo do último mousedown dentro do collab-col-body. Se o mousedown partiu de
    // um card, o click subsequente (mesmo que o cursor tenha saído do card) não deve
    // abrir o planner — o usuário estava interagindo com um agendamento existente.
    const colBodyMouseDownTargetRef = useRef(null);
    
    // Modal states
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showStartModal, setShowStartModal] = useState(false);
    const [showCompleteModal, setShowCompleteModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showNoShowModal, setShowNoShowModal] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    // A operação da agenda é orientada por colaborador; não há alternância de visualização nesta tela.
    const viewMode = 'collaborator';
    const [calendarView, setCalendarView] = useState('week');
    // Cancelado não é desenhado por padrão; o horário está livre de novo.
    const [showCanceledAppointments, setShowCanceledAppointments] = useState(standalone && searchParams.get('canceled') === 'true');
    const [collaboratorDate, setCollaboratorDate] = useState(initialDate);
    const calendarLocale = (i18n.language || 'pt-br').toLowerCase();
    // No celular o calendário abre centralizado em tela cheia: ancorado no botão, ele
    // nasceria colado na borda e parte dos dias ficaria fora do viewport.
    const isNarrowScreen = useMediaQuery('(max-width: 767px)');
    const [slotPlannerOpen, setSlotPlannerOpen] = useState(false);
    const [quickCustomerOpen, setQuickCustomerOpen] = useState(false);
    // Marca que o agendamento rápido foi escondido só para o cadastro de cliente aparecer, e
    // que deve voltar quando o cadastro fechar (salvando ou cancelando).
    const plannerHiddenForCustomerRef = useRef(false);
    const [slotPlannerDate, setSlotPlannerDate] = useState(null);
    const [slotPlannerLoading, setSlotPlannerLoading] = useState(false);
    const [slotPlannerSubmitting, setSlotPlannerSubmitting] = useState(false);
    const [slotPlannerError, setSlotPlannerError] = useState('');
    const [plannerBranchOptions, setPlannerBranchOptions] = useState([]);
    const [plannerEmployeeOptions, setPlannerEmployeeOptions] = useState([]);
    const [plannerServiceOptions, setPlannerServiceOptions] = useState([]);
    const [plannerSlots, setPlannerSlots] = useState([]);
    const [plannerCustomer, setPlannerCustomer] = useState(null);
    const [plannerCustomerSubscription, setPlannerCustomerSubscription] = useState(null);
    const [plannerBranch, setPlannerBranch] = useState(null);
    const [plannerEmployee, setPlannerEmployee] = useState(null);
    // Um agendamento rápido pode ter vários serviços (1 visita, N serviços). Guardamos uma lista.
    const [plannerServices, setPlannerServices] = useState([]);
    // Reserva a receber junto com o agendamento novo (só aparece quando o serviço a exige).
    const [plannerDeposit, setPlannerDeposit] = useState(buildDepositDraft());
    const [reschedulingAppointment, setReschedulingAppointment] = useState(null);
    // Atendimento sendo corrigido no painel (cliente, profissional, serviços, preço, observação).
    // Separado de reschedulingAppointment porque ali o objetivo é trocar o horário, e aqui é
    // justamente mantê-lo: quem edita dados não quer perder o horário já combinado.
    const [editingAppointment, setEditingAppointment] = useState(null);
    // Modo específico acionado pelo ícone do card: somente serviços, sem alterar a agenda.
    const [servicesOnlyAppointment, setServicesOnlyAppointment] = useState(false);
    const [plannerNotes, setPlannerNotes] = useState('');
    const [useSpecificQuickTime, setUseSpecificQuickTime] = useState(false);
    const [specificQuickStartTime, setSpecificQuickStartTime] = useState('');
    const [specificQuickEndTime, setSpecificQuickEndTime] = useState('');

    useEffect(() => {
        const customerId = plannerCustomer?.value;
        if (!customerId) {
            setPlannerCustomerSubscription(null);
            return undefined;
        }

        let requestIsCurrent = true;
        setPlannerCustomerSubscription(null);

        // O CustomerSelect traz apenas o resumo do cliente. Buscamos a assinatura ao
        // selecioná-lo para reaproveitar o mesmo detalhamento de plano já usado no cadastro
        // de assinantes, sem duplicar regras de produtos, periodicidade e limites.
        axiosInstance.get(`${Api.subscription.subscription}/customer/id/${customerId}`)
            .then(({ data }) => {
                if (requestIsCurrent) {
                    setPlannerCustomerSubscription(data || null);
                }
            })
            .catch(() => {
                if (requestIsCurrent) {
                    setPlannerCustomerSubscription(null);
                }
            });

        return () => {
            requestIsCurrent = false;
        };
    }, [plannerCustomer?.value]);
    // Encaixe: atendimento por cima de um horário já ocupado. Guarda o agendamento de
    // referência e o horário escolhido, porque aqui a lista de horários livres não serve: o
    // horário do encaixe é exatamente um que já está tomado.
    const [encaixeMode, setEncaixeMode] = useState(false);
    const [encaixeReference, setEncaixeReference] = useState(null);
    const [encaixeTime, setEncaixeTime] = useState('');
    const [selectedCollaboratorFilter, setSelectedCollaboratorFilter] = useState(null);
    // Todos os colaboradores da filial selecionada. Usado para exibir uma coluna por
    // colaborador na agenda mesmo sem agendamento no dia (antes vinha só dos agendamentos).
    const [boardEmployees, setBoardEmployees] = useState([]);
    const [appointmentBlocks, setAppointmentBlocks] = useState([]);
    const [showBlockModal, setShowBlockModal] = useState(false);
    const [blockSubmitting, setBlockSubmitting] = useState(false);
    const [blockBranchEmployeeOptions, setBlockBranchEmployeeOptions] = useState([]);
    const [blockEmployeesLoading, setBlockEmployeesLoading] = useState(false);
    const [blockDraft, setBlockDraft] = useState({
        employeeId: null,
        employeeLabel: '',
        branchId: null,
        branchLabel: '',
        startDate: '',
        endDate: '',
        reason: 'Reunião',
        notes: ''
    });

    // Duração e preço totais somando todos os serviços selecionados no agendamento rápido.
    // Zero é um tempo cadastrado como outro qualquer: um serviço que não reserva janela
    // soma zero. Só a duração ausente (serviço legado) cai no padrão do sistema.
    const plannerTotalDuration = useMemo(
        () => plannerServices.reduce((sum, option) => {
            const registered = Number(option.service?.duration);
            return sum + (Number.isFinite(registered) ? Math.max(registered, 0) : DEFAULT_SERVICE_DURATION_MINUTES);
        }, 0),
        [plannerServices]
    );
    // A sugestão de tipo e valor segue os serviços escolhidos. Só reinicia quando muda o conjunto
    // de serviços que exigem reserva, para não apagar o que a recepção já digitou ao trocar um
    // serviço que não tem nada a ver com a reserva.
    const plannerDepositServicesKey = plannerServices
        .filter(option => option.service?.requiresDeposit)
        .map(option => option.value)
        .join(',');
    useEffect(() => {
        setPlannerDeposit(buildDepositDraft(plannerServices));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [plannerDepositServicesKey]);

    const plannerTotalPrice = useMemo(
        () => plannerServices.reduce((sum, option) => sum + (Number(option.service?.price) || 0), 0),
        [plannerServices]
    );
    // Basta um serviço de preço aberto para o total da visita deixar de ser fechado: quem
    // está marcando precisa saber disso na hora de responder "quanto vai ficar?".
    const plannerHasPriceFrom = useMemo(
        () => plannerServices.some(option => option.service?.priceFrom === true),
        [plannerServices]
    );

    // A grade vem vazia tanto quando o dia está lotado quanto quando o profissional não
    // trabalha nele. Sem distinguir os dois casos, o operador fica procurando um horário
    // livre que não existe em dia nenhum daquele profissional.
    const isPlannerEmployeeDayOff = useMemo(
        () => Boolean(plannerEmployee?.employee && slotPlannerDate
            && !worksOnDate(plannerEmployee.employee.workDays, new Date(slotPlannerDate))),
        [plannerEmployee, slotPlannerDate]
    );

    // Mesmo motivo da folga: sem isso a grade vazia das férias pareceria dia lotado.
    const isPlannerEmployeeOnVacation = useMemo(
        () => Boolean(plannerEmployee?.employee && slotPlannerDate
            && isOnVacationOn(plannerEmployee.employee, new Date(slotPlannerDate))),
        [plannerEmployee, slotPlannerDate]
    );

    useEffect(() => {
        moment.locale(calendarLocale);
    }, [calendarLocale]);

    // Trava a rolagem do fundo enquanto o painel lateral está aberto.
    //
    // O valor anterior NÃO é guardado para ser restaurado, e isso é deliberado. Reagendar e
    // editar abrem o painel a partir do modal de detalhes, que é um Modal do react-bootstrap e
    // mantém 'hidden' no body enquanto está aberto. Guardar o valor anterior capturava esse
    // 'hidden' temporário; o modal fechava em seguida (o bootstrap restaurava o dele), e ao
    // fechar o painel a limpeza reaplicava 'hidden'. A página ficava sem rolagem e sem barra
    // até recarregar. Limpar o estilo inline devolve o controle a quem mais estiver aberto.
    useEffect(() => {
        if (!slotPlannerOpen) return undefined;

        document.body.style.overflow = 'hidden';
        document.body.classList.add('appointment-slot-planner-open');

        return () => {
            document.body.style.overflow = '';
            document.body.classList.remove('appointment-slot-planner-open');
        };
    }, [slotPlannerOpen]);

    useEffect(() => {
        if (!companyUuid) return;

        getCompanyInfo(companyUuid)
            .then((response) => {
                const branches = Array.isArray(response.data?.branches) ? response.data.branches : [];
                
                const filteredBranches = selectedBranch?.id
                    ? branches.filter(b => String(b.id) === String(selectedBranch.id))
                    : branches;
                
                const options = filteredBranches.map((branch) =>
                    buildQuickOption(branch.id, branch.name, { branch })
                );

                setPlannerBranchOptions(options);
                setPlannerBranch((currentBranch) => {
                    if (currentBranch && options.some((option) => option.value === currentBranch.value)) {
                        return currentBranch;
                    }

                    if (selectedBranch) {
                        const matchedBranch = options.find((option) => option.value === selectedBranch.id);
                        if (matchedBranch) {
                            return matchedBranch;
                        }
                    }

                    return options.length === 1 ? options[0] : null;
                });
            })
            .catch((error) => {
                console.error('Erro ao carregar filiais do booking:', error);
            });
    }, [companyUuid, selectedBranch]);

    // Usa o endpoint interno e restringe explicitamente pela filial selecionada. O filtro de
    // acesso do backend continua sendo aplicado em conjunto com este recorte operacional.
    // Quem está com "Presta serviço?" desligado (recepção, gestão) não ganha coluna: a flag
    // diz justamente se a pessoa aparece como prestadora na agenda. A regra vale também para
    // as colunas e opções que nascem dos agendamentos (ver isServiceProvider).
    useEffect(() => {
        let requestIsCurrent = true;
        const filters = selectedBranch?.id
            ? [{
                field: 'branches.id',
                value: selectedBranch.id,
                operation: SearchOperation.EQUAL
            }]
            : [];

        setBoardEmployees([]);
        axiosInstance.post(`${Api.main.employee}/findByFilters`, { filters })
            .then((response) => {
                if (requestIsCurrent) {
                    const employees = Array.isArray(response.data) ? response.data : [];
                    setBoardEmployees(employees.filter(isServiceProvider));
                }
            })
            .catch(() => {
                if (requestIsCurrent) {
                    setBoardEmployees([]);
                }
            });

        // Evita que uma resposta da filial anterior sobrescreva uma troca rápida de unidade.
        return () => {
            requestIsCurrent = false;
        };
    }, [selectedBranch?.id]);

    useEffect(() => {
        if (!companyUuid || !plannerBranch?.value) {
            setPlannerEmployeeOptions([]);
            setPlannerServiceOptions([]);
            setPlannerEmployee(null);
            setPlannerServices([]);
            return;
        }

        setSlotPlannerLoading(true);
        Promise.all([
            getEmployeesByBranch(companyUuid, plannerBranch.value),
            // A agenda interna lista o catálogo inteiro, inclusive o que não aparece no site:
            // cortesia e retoque são atendimentos que a recepção lança.
            getServicesByBranch(companyUuid, plannerBranch.value, true)
        ])
            .then(([employeesResponse, servicesResponse]) => {
                const employees = Array.isArray(employeesResponse.data) ? employeesResponse.data : [];
                const services = Array.isArray(servicesResponse.data) ? servicesResponse.data : [];

                const employeeOptions = employees.map((employee) =>
                    buildQuickOption(
                        employee.id,
                        employee.name || employee.fullName || [employee.firstName, employee.lastName].filter(Boolean).join(' ') || 'Colaborador',
                        { employee }
                    )
                );
                const serviceOptions = services.map((service) =>
                    buildQuickOption(service.id, service.name, { service })
                );

                setPlannerEmployeeOptions(employeeOptions);
                setPlannerServiceOptions(serviceOptions);
                setPlannerEmployee((currentEmployee) => {
                    if (currentEmployee && employeeOptions.some((option) => option.value === currentEmployee.value)) {
                        return currentEmployee;
                    }
                    if (reschedulingAppointment?.employee) {
                        const matchedEmp = employeeOptions.find((option) => String(option.value) === String(reschedulingAppointment.employee.id));
                        if (matchedEmp) return matchedEmp;
                    }
                    return null;
                });
                setPlannerServices((currentServices) => {
                    // Mantém apenas os serviços ainda válidos para a filial recém-carregada.
                    const stillValid = currentServices.filter((option) =>
                        serviceOptions.some((serviceOption) => serviceOption.value === option.value));
                    if (stillValid.length > 0) {
                        return stillValid;
                    }
                    if (reschedulingAppointment?.service) {
                        const matchedSrv = serviceOptions.find((option) => String(option.value) === String(reschedulingAppointment.service.id));
                        if (matchedSrv) return [matchedSrv];
                    }
                    return [];
                });
            })
            .catch((error) => {
                console.error('Erro ao carregar profissionais/serviços:', error);
                setSlotPlannerError('Não foi possível carregar os dados para montar o agendamento rápido.');
            })
            .finally(() => {
                setSlotPlannerLoading(false);
            });
    }, [companyUuid, plannerBranch?.value, reschedulingAppointment]);

    useEffect(() => {
        if (!slotPlannerOpen) return;

        if (!companyUuid || !slotPlannerDate || !plannerBranch?.value || !plannerEmployee?.value || plannerServices.length === 0) {
            setPlannerSlots([]);
            return;
        }

        setSlotPlannerLoading(true);
        setSlotPlannerError('');

        const mapAvailableSlots = (response) => {
            const slots = Array.isArray(response.data) ? response.data : [];
            const parseTimeMinutes = (value) => {
                const match = String(value || '').match(/^(\d{1,2}):(\d{2})/);
                return match ? Number(match[1]) * 60 + Number(match[2]) : null;
            };
            const lunchStart = parseTimeMinutes(plannerEmployee?.employee?.lunchStartTime);
            const lunchEnd = parseTimeMinutes(plannerEmployee?.employee?.lunchEndTime);
            const isEmployeeLunch = (slot) => {
                if (lunchStart == null || lunchEnd == null || lunchEnd <= lunchStart) return false;
                const slotStart = parseTimeMinutes(formatSlotTime(slot));
                const slotEnd = slot?.endTime
                    ? parseTimeMinutes(formatSlotTime({ startTime: slot.endTime }))
                    : slotStart;
                return slotStart != null && slotEnd != null && slotStart < lunchEnd && slotEnd > lunchStart;
            };

            return slots.filter((slot) => slot?.available || slot?.unavailabilityReason === 'PAST'
                || slot?.unavailabilityReason === 'BREAK' || isEmployeeLunch(slot))
                .map((slot) => ({
                    ...slot,
                    // O cadastro do almoço do colaborador é a fonte local de segurança:
                    // mesmo que a API não classifique o intervalo, ele nunca vira horário livre.
                    lunchBreak: slot?.unavailabilityReason === 'BREAK' || isEmployeeLunch(slot),
                    time: formatSlotTime(slot)
                }))
                .sort((slotA, slotB) => slotA.time.localeCompare(slotB.time));
        };

        getAvailableSlots(
            companyUuid,
            plannerBranch.value,
            plannerEmployee.value,
            formatDateToYYYYMMDD(slotPlannerDate),
            // A duração considerada é a soma de todos os serviços selecionados, inclusive
            // quando ela é zero.
            plannerTotalDuration
        )
            .then((response) => {
                setPlannerSlots(mapAvailableSlots(response));
            })
            .catch((error) => {
                console.error('Erro ao carregar horários disponíveis:', error);
                setPlannerSlots([]);
                setSlotPlannerError('Não foi possível carregar os horários disponíveis para esse dia.');
            })
            .finally(() => {
                setSlotPlannerLoading(false);
            });
    }, [slotPlannerOpen, companyUuid, slotPlannerDate, plannerBranch?.value, plannerEmployee?.value, plannerServices, plannerTotalDuration]);

    // Carrega informações da empresa para obter o uuid usado nas consultas públicas da agenda.
    const loadCompanyInfo = useCallback(() => {
        axiosInstance.get(Api.registration.company_info)
            .then((resp) => {
                if (resp.data && resp.data.uuid) {
                    setCompanyUuid(resp.data.uuid);
                }
            })
            .catch((error) => {
                console.error("Erro ao buscar informações da empresa:", error);
            });
    }, []);

    // Função para atualizar apenas o agendamento selecionado após ações
    const refreshSelectedAppointment = () => {
        const appointmentToRefresh = selectedAppointment || selectedAppointmentRef.current;
        if (appointmentToRefresh && appointmentToRefresh.id) {
            // Pequeno delay para permitir que o calendário atualize primeiro
            setTimeout(() => {
                axiosInstance.get(`${Api.appointment.appointment}/${appointmentToRefresh.id}`)
                    .then(response => {
                        selectedAppointmentRef.current = response.data;
                        setSelectedAppointment(response.data);
                    })
                    .catch(error => {
                        console.error('Error refreshing selected appointment:', error);
                        // Se der erro, pode ser que o agendamento foi deletado
                        selectedAppointmentRef.current = null;
                        setSelectedAppointment(null);
                    });
            }, 100);
        }
    };

    /**
     * Pede uma releitura dos agendamentos.
     *
     * Existe porque chamar loadAppointments() direto de dentro de um .then() usa o closure do
     * render em que a ação começou: se a data mudou no meio (reagendar leva a agenda para o dia
     * novo), a busca sai com a data antiga e a tela fica mostrando o estado anterior até alguém
     * recarregar. Mexer neste contador faz o efeito refazer a busca já com a data atual.
     */
    const refreshAppointments = useCallback(() => {
        setAppointmentsRefreshToken((current) => current + 1);
    }, []);

    const loadAppointments = useCallback(() => {
        // Marca esta busca como a mais recente. Só a resposta dela pode alterar a tela.
        const requestId = appointmentsRequestIdRef.current + 1;
        appointmentsRequestIdRef.current = requestId;

        // Uma resposta só vale se nenhuma busca mais nova tiver sido disparada depois dela.
        const isCurrentRequest = () => appointmentsRequestIdRef.current === requestId;

        setShowLoading(true);

        // Test different date formats that Spring Boot LocalDateTime.parse accepts
        const startOfMonth = moment(currentDate).startOf('month').format('YYYY-MM-DDTHH:mm:ss');
        const endOfMonth = moment(currentDate).endOf('month').format('YYYY-MM-DDTHH:mm:ss');


        
        axiosInstance.get(Api.appointment.schedule, {
            params: {
                startDate: startOfMonth,
                endDate: endOfMonth,
                // Sem a filial o backend devolvia o mês de todas as unidades e a tela
                // descartava o excedente depois de já ter trafegado tudo.
                branchId: selectedBranch?.id || undefined
            }
        })
        .then((response) => {
            // Resposta atrasada de uma data que o operador já abandonou: descarta.
            if (!isCurrentRequest()) {
                return;
            }

            const appointmentEvents = response.data.map((apt) => {
                const normalizedPrice = getAppointmentPriceValue(apt);

                return {
                    id: apt.id,
                    title: `${getDisplayName(apt.customer, 'Cliente')} - ${getServicesLabel(apt, 'Serviço')}`,
                    start: new Date(apt.scheduledDate),
                    end: new Date(moment(apt.scheduledDate).add(getDurationInMinutes(apt), 'minutes')),
                    resource: {
                        ...apt,
                        price: normalizedPrice
                    },
                    status: apt.appointmentStatus,
                    className: getStatusClass(apt.appointmentStatus)
                };
            });
            setAppointments(appointmentEvents);
            
            // Preserva a seleção após o reload dos appointments
            const currentlySelected = selectedAppointmentRef.current;
            if (currentlySelected && currentlySelected.id) {
                const updatedAppointment = appointmentEvents.find(apt => apt.resource.id === currentlySelected.id);
                if (updatedAppointment) {
                    selectedAppointmentRef.current = updatedAppointment.resource;
                    setSelectedAppointment(updatedAppointment.resource);
                } else {
                    selectedAppointmentRef.current = null;
                    setSelectedAppointment(null);
                }
            }
        })
        .catch(error => {
            // Erro de busca abandonada não interessa ao operador, que já está em outra data.
            if (!isCurrentRequest()) {
                return;
            }

            console.error('Error loading appointments:', error);
            errorAlert(error);
        })
        .finally(() => {
            // Mantém o loading enquanto a busca mais recente ainda estiver em voo.
            if (!isCurrentRequest()) {
                return;
            }

            setShowLoading(false);
        });
    }, [currentDate, appointmentsRefreshToken, selectedBranch?.id]);

    // O expediente da filial define a faixa de horas desenhada na agenda. Sem ele a grade cai no dia
    // inteiro (00:00–23:59) e o operador precisa rolar por horas em que a filial nem abre.
    const loadBranchWorkingHours = useCallback(() => {
        // Sem filial escolhida no seletor principal, a agenda mostra as filiais todas — e o
        // expediente precisa continuar valendo. Antes, esse caminho zerava a lista e a grade
        // caía em 00:00–23:59, que é o "não está respeitando o horário de funcionamento":
        // bastava operar com o contexto em "todas" para perder o recorte.
        // Com várias filiais, a grade cobre da abertura mais cedo ao fechamento mais tarde
        // (ver getWorkingWindow, que já une as janelas), pois um quadro só precisa caber o
        // expediente de todas as unidades exibidas.
        const request = selectedBranch?.id
            ? axiosInstance.get(`${Api.main.branch_working_hours}/branch/${selectedBranch.id}`)
            : axiosInstance.get(Api.main.branch_working_hours);

        request
            .then((response) => setBranchWorkingHours(Array.isArray(response.data) ? response.data : []))
            .catch((error) => {
                // Falha aqui não impede operar a agenda: sem expediente, a grade volta ao dia inteiro.
                console.error('Erro ao carregar horários de funcionamento da filial:', error);
                setBranchWorkingHours([]);
            });
    }, [selectedBranch?.id]);

    // Mês em tela, com uma semana de folga para a visão semanal que atravessa a virada do mês. As
    // datas viram texto para a busca só refazer quando o mês muda, e não a cada dia navegado.
    const specialDaysRange = useMemo(() => {
        const dates = [moment(currentDate), moment(collaboratorDate)];
        return {
            start: moment.min(dates).clone().startOf('month').subtract(7, 'days').format('YYYY-MM-DD'),
            end: moment.max(dates).clone().endOf('month').add(7, 'days').format('YYYY-MM-DD')
        };
    }, [currentDate, collaboratorDate]);

    useEffect(() => {
        axiosInstance.get(Api.appointment.special_day, {
            params: {
                startDate: specialDaysRange.start,
                endDate: specialDaysRange.end,
                ...(selectedBranch?.id ? { branchId: selectedBranch.id } : {})
            }
        })
            .then((response) => setSpecialDays(Array.isArray(response.data) ? response.data : []))
            .catch((error) => {
                // Sem as datas especiais a agenda segue o horário semanal; o servidor continua
                // recusando o agendamento no feriado, então a falha aqui não abre brecha.
                console.error('Erro ao carregar datas especiais da filial:', error);
                setSpecialDays([]);
            });
    }, [specialDaysRange.start, specialDaysRange.end, selectedBranch?.id]);

    const loadAppointmentBlocks = useCallback(() => {
        const day = moment(collaboratorDate);
        axiosInstance.get(Api.appointment.block, {
            params: {
                startDate: day.clone().startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
                endDate: day.clone().endOf('day').format('YYYY-MM-DDTHH:mm:ss'),
                ...(selectedBranch?.id ? { branchId: selectedBranch.id } : {})
            }
        })
            .then((response) => setAppointmentBlocks(Array.isArray(response.data) ? response.data : []))
            .catch((error) => {
                console.error('Erro ao carregar bloqueios da agenda:', error);
                setAppointmentBlocks([]);
            });
    }, [collaboratorDate, selectedBranch?.id]);

    useEffect(() => {
        // O "teste de conectividade" que existia aqui chamava GET /appointment, que devolve
        // a agenda inteira da empresa sem filtro e sem paginação. Numa base com anos de
        // histórico era ele quem estourava o tempo de resposta da tela, e o resultado era
        // descartado. As chamadas abaixo já revelam qualquer falha de conexão.
        loadCompanyInfo();
        loadBranchWorkingHours();
    }, [loadCompanyInfo, loadBranchWorkingHours]);

    // Os agendamentos vêm por mês, então só precisam ser rebuscados quando o mês muda.
    // Junto com os bloqueios (que são por dia) isso refazia a busca a cada troca de dia,
    // deixando respostas de meses diferentes correndo em paralelo.
    useEffect(() => {
        loadAppointments();
    }, [loadAppointments]);

    useEffect(() => {
        loadAppointmentBlocks();
    }, [loadAppointmentBlocks]);

    const openBlockModal = () => {
        const start = moment(slotPlannerDate || collaboratorDate);
        const normalizedStart = start.seconds(0).milliseconds(0);
        // O colaborador pode ter vindo do clique na coluna ou do filtro do quadro.
        // Guardamos os ids no rascunho para uma recarga assíncrona dos selects não apagar
        // a seleção entre a abertura do modal e a confirmação do bloqueio.
        const employee = plannerEmployee ?? selectedCollaboratorFilter;
        const branch = plannerBranch ?? (selectedBranch
            ? { value: selectedBranch.id, label: selectedBranch.name }
            : null);
        setBlockDraft({
            employeeId: employee?.value ?? null,
            employeeLabel: employee?.label ?? '',
            branchId: branch?.value ?? null,
            branchLabel: branch?.label ?? '',
            startDate: normalizedStart.format('YYYY-MM-DDTHH:mm'),
            endDate: normalizedStart.clone().add(60, 'minutes').format('YYYY-MM-DDTHH:mm'),
            reason: 'Reunião',
            notes: ''
        });
        // O bloqueio é uma ação alternativa ao agendamento rápido. Fechar o painel evita
        // duas camadas de formulário abertas e deixa o operador focado no bloqueio.
        closeSlotPlanner();
        setShowBlockModal(true);
    };

    const createAppointmentBlock = () => {
        if (!blockDraft.employeeId) {
            warnAlert('Selecione o colaborador.');
            return;
        }
        if (!blockDraft.branchId) {
            warnAlert('Selecione a filial antes de registrar o bloqueio.');
            return;
        }
        if (!moment(blockDraft.endDate).isAfter(moment(blockDraft.startDate))) {
            warnAlert('O fim do período deve ser posterior ao início.');
            return;
        }
        setBlockSubmitting(true);
        axiosInstance.post(Api.appointment.block, {
            employeeId: blockDraft.employeeId,
            branchId: blockDraft.branchId,
            startDate: `${blockDraft.startDate}:00`,
            endDate: `${blockDraft.endDate}:00`,
            reason: blockDraft.reason,
            notes: blockDraft.notes
        })
            .then(() => {
                successAlert(blockDraft.reason === 'Folga'
                    ? 'Folga registrada com sucesso.'
                    : 'Bloqueio registrado com sucesso.');
                setShowBlockModal(false);
                closeSlotPlanner();
                loadAppointmentBlocks();
            })
            .catch((error) => errorObjectAlert(error))
            .finally(() => setBlockSubmitting(false));
    };

    const cancelAppointmentBlock = (block) => {
        warnAlertYesOrNo(
            `Deseja realmente cancelar o bloqueio "${block.reason}" de ${moment(block.startDate).format('HH:mm')} às ${moment(block.endDate).format('HH:mm')}?`,
            () => {
                axiosInstance.patch(`${Api.appointment.block}/${block.id}/cancel`)
                    .then(() => {
                        successAlert('Bloqueio cancelado com sucesso.');
                        loadAppointmentBlocks();
                    })
                    .catch((error) => errorObjectAlert(error));
            }
        );
    };

    // Os colaboradores do bloqueio vêm da filial escolhida no próprio bloqueio, e não da filial
    // do agendamento rápido: antes, trocar a filial no modal mantinha a lista da outra unidade.
    // Usa o mesmo endpoint do agendamento rápido, então valem as mesmas regras do quadro: só
    // quem está vinculado à filial e presta serviço.
    useEffect(() => {
        if (!showBlockModal || !companyUuid || !blockDraft.branchId) {
            setBlockBranchEmployeeOptions([]);
            return undefined;
        }

        let requestIsCurrent = true;
        setBlockEmployeesLoading(true);
        getEmployeesByBranch(companyUuid, blockDraft.branchId)
            .then((response) => {
                if (!requestIsCurrent) {
                    return;
                }
                const employees = Array.isArray(response.data) ? response.data : [];
                const options = employees.map((employee) => buildQuickOption(
                    employee.id,
                    employee.name || employee.fullName || [employee.firstName, employee.lastName].filter(Boolean).join(' ') || 'Colaborador',
                    { employee }
                ));
                setBlockBranchEmployeeOptions(options);
                // Colaborador de outra filial não pode ficar selecionado depois da troca.
                setBlockDraft((current) => (
                    current.employeeId && !options.some((option) => String(option.value) === String(current.employeeId))
                        ? { ...current, employeeId: null, employeeLabel: '' }
                        : current
                ));
            })
            .catch(() => {
                if (requestIsCurrent) {
                    setBlockBranchEmployeeOptions([]);
                }
            })
            .finally(() => {
                if (requestIsCurrent) {
                    setBlockEmployeesLoading(false);
                }
            });

        // Evita que a resposta de uma filial anterior sobrescreva a troca rápida de unidade.
        return () => {
            requestIsCurrent = false;
        };
    }, [showBlockModal, companyUuid, blockDraft.branchId]);

    // O bloqueio pode ser aberto a partir da coluna do quadro antes de a lista da filial
    // carregar. Mantemos a opção atual no select para não perder a seleção nesse intervalo.
    const blockEmployeeOptions = useMemo(() => {
        if (!blockDraft.employeeId || blockBranchEmployeeOptions.some(
            (option) => String(option.value) === String(blockDraft.employeeId))) {
            return blockBranchEmployeeOptions;
        }

        return [
            { value: blockDraft.employeeId, label: blockDraft.employeeLabel },
            ...blockBranchEmployeeOptions
        ];
    }, [blockDraft.employeeId, blockDraft.employeeLabel, blockBranchEmployeeOptions]);

    const blockEmployeeValue = blockDraft.employeeId
        ? { value: blockDraft.employeeId, label: blockDraft.employeeLabel }
        : null;

    // O BranchSelect espera a opção inteira. Montamos a partir do rascunho porque a lista do
    // componente carrega por conta própria, e antes de ela chegar o react-select ainda exibe
    // o rótulo guardado aqui, em vez de deixar o campo aparentemente vazio.
    const blockBranchValue = blockDraft.branchId
        ? { value: blockDraft.branchId, label: blockDraft.branchLabel }
        : null;

    const getStatusClass = (status) => {
        switch (status) {
            case 'SCHEDULED': return 'event-scheduled';
            case 'CONFIRMED': return 'event-info';
            case 'IN_PROGRESS': return 'event-primary';
            case 'COMPLETED': return 'event-success';
            case 'CANCELED': return 'event-danger';
            case 'NO_SHOW': return 'event-dark';
            default: return 'event-secondary';
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'SCHEDULED': return <Badge style={{ backgroundColor: '#7048c4' }}>Agendado</Badge>;
            case 'CONFIRMED': return <Badge bg="info">Confirmado</Badge>;
            case 'IN_PROGRESS': return <Badge bg="primary">Em Andamento</Badge>;
            case 'COMPLETED': return <Badge bg="secondary">Concluído</Badge>;
            case 'CANCELED': return <Badge bg="warning" text="dark">Cancelado</Badge>;
            case 'NO_SHOW': return <Badge bg="danger">Não Compareceu</Badge>;
            default: return <Badge bg="secondary">{status}</Badge>;
        }
    };

    const handleSelectEvent = (event) => {
        // Previne re-seleção do mesmo agendamento para evitar re-renders desnecessários
        if (selectedAppointment && selectedAppointment.id === event.resource.id) {
            return;
        }
        
        // Usa tanto state quanto ref para preservar
        selectedAppointmentRef.current = event.resource;
        setSelectedAppointment(event.resource);
    };

    const handleConfirm = () => {
        if (!selectedAppointment) return;

        axiosInstance.patch(`${Api.appointment.appointment}/${selectedAppointment.id}/confirm`)
            .then(() => {
                successAlert(t('message.operation_successfully'));
                refreshAppointments(); // Atualiza o calendário
                refreshSelectedAppointment(); // Atualiza apenas o agendamento selecionado
                setShowConfirmModal(false);
            })
            .catch(error => showError(error));
    };

    const handleStart = () => {
        if (!selectedAppointment) return;

        axiosInstance.patch(`${Api.appointment.appointment}/${selectedAppointment.id}/start`)
            .then(() => {
                successAlert(t('message.operation_successfully'));
                refreshAppointments(); // Atualiza o calendário
                refreshSelectedAppointment(); // Atualiza apenas o agendamento selecionado
                setShowStartModal(false);
            })
            .catch(error => showError(error));
    };

    const handleComplete = () => {
        if (!selectedAppointment) return;

        axiosInstance.patch(`${Api.appointment.appointment}/${selectedAppointment.id}/complete`)
            .then(() => {
                successAlert(t('message.operation_successfully'));
                refreshAppointments(); // Atualiza o calendário
                refreshSelectedAppointment(); // Atualiza apenas o agendamento selecionado
                setShowCompleteModal(false);
            })
            .catch(error => showError(error));
    };

    const showError = (error) => {
        errorAlert(error.response?.data?.message || error.message)
    }

    const handleCancel = () => {
        if (!selectedAppointment) return;
        if (!cancelReason.trim()) {
            warnAlert('Por favor, informe o motivo do cancelamento');
            return;
        }

        axiosInstance.patch(`${Api.appointment.appointment}/${selectedAppointment.id}/cancel`, {
            reason: cancelReason
        })
        .then(() => {
            successAlert(t('message.operation_successfully'));
            refreshAppointments(); // A atualização do selectedAppointment acontece no loadAppointments
            setShowCancelModal(false);
            setCancelReason('');
        })
        .catch(error => errorAlert(error));
    };

    const handleNoShow = () => {
        if (!selectedAppointment) return;

        axiosInstance.patch(`${Api.appointment.appointment}/${selectedAppointment.id}/no-show`)
            .then(() => {
                successAlert(t('message.operation_successfully'));
                refreshAppointments(); // Atualiza o calendário
                refreshSelectedAppointment(); // Atualiza apenas o agendamento selecionado
                setShowNoShowModal(false);
            })
            .catch(error => errorAlert(error));
    };

    const eventStyleGetter = (event) => {
        const baseStyle = {
            className: event.className,
            style: {}
        };
        
        const clubMember = event.resource?.customer?.clubMember;

        if (clubMember) {
            baseStyle.style = {
                background: 'linear-gradient(135deg, #f7d070 0%, #fdf6cf 40%, #dfb244 70%, #c39627 100%)',
                backgroundColor: '#dfb244',
                borderColor: '#c39627',
                color: '#3b2501'
            };
        }

        // O status operacional tem prioridade sobre o destaque dourado do clube. Sem esta
        // sobrescrita inline, um cliente do clube continuava dourado depois de concluído e o
        // operador não percebia a mudança de estado no quadro.
        if (event.status === 'COMPLETED') {
            // Mesma distinção do quadro por colaborador: cinza é o atendimento encerrado com
            // a venda registrada; sem venda gerada, a cor sinaliza que ainda falta fazê-la.
            const pendingSale = !event.resource?.saleGenerated;

            baseStyle.style = pendingSale
                ? {
                    background: PENDING_SALE_SURFACE.background,
                    backgroundColor: PENDING_SALE_SURFACE.background,
                    borderColor: PENDING_SALE_SURFACE.border,
                    color: PENDING_SALE_SURFACE.text
                }
                : {
                    background: '#9ca3af',
                    backgroundColor: '#9ca3af',
                    borderColor: '#9ca3af',
                    color: '#1f2937'
                };
        }

        if (event.status === 'NO_SHOW') {
            baseStyle.style = {
                backgroundColor: '#dc3545',
                borderColor: '#b02a37',
                color: 'white'
            };
        }

        // Adiciona estilo inline específico para eventos cancelados para garantir que apareçam em vermelho
        if (event.status === 'CANCELED') {
            baseStyle.style = {
                backgroundColor: '#f59e0b',
                borderColor: '#d97706',
                color: '#451a03'
            };
        }
        
        return baseStyle;
    };

    const handleCollaboratorDateChange = (newDate) => {
        const oldMonth = moment(collaboratorDate).month();
        const oldYear = moment(collaboratorDate).year();
        const newMonth = moment(newDate).month();
        const newYear = moment(newDate).year();
        setCollaboratorDate(newDate);
        if (newMonth !== oldMonth || newYear !== oldYear) {
            setCurrentDate(newDate);
        }
    };

    const handleCalendarNavigate = (newDate) => {
        setCurrentDate(newDate);
    };

    /**
     * Filial que o planner assume quando quem abriu não sabe dizer qual é: a selecionada no
     * topo ou, se a empresa só tem uma, ela mesma. Sem candidata, devolve null para o
     * operador escolher, em vez de chutar uma unidade.
     */
    const resolveDefaultPlannerBranch = () => {
        if (selectedBranch) {
            const matched = plannerBranchOptions.find((option) => option.value === selectedBranch.id);
            return matched ?? { value: selectedBranch.id, label: selectedBranch.name, branch: selectedBranch };
        }

        return plannerBranchOptions.length === 1 ? plannerBranchOptions[0] : null;
    };

    const openSlotPlanner = (date, { encaixe = false } = {}) => {
        setEncaixeMode(encaixe);
        if (!encaixe) setEncaixeReference(null);
        setSlotPlannerDate(new Date(date));
        setSlotPlannerError('');
        setPlannerSlots([]);
        setUseSpecificQuickTime(false);
        setSpecificQuickStartTime('');
        setSpecificQuickEndTime('');
        // Só completa a filial se quem abriu não definiu nenhuma. O reagendamento e o clique
        // na coluna do colaborador definem antes de chamar, e a escolha deles tem prioridade.
        setPlannerBranch((current) => current ?? resolveDefaultPlannerBranch());
        setSlotPlannerOpen(true);
    };

    const filteredAppointments = useMemo(() => {
        if (!selectedBranch?.id) {
            return appointments;
        }

        const selectedBranchId = String(selectedBranch.id);

        return appointments.filter((event) => String(event.resource?.branch?.id || '') === selectedBranchId);
    }, [appointments, selectedBranch]);

    const collaboratorFilterOptions = useMemo(() => {
        const uniqueEmployees = new Map();

        // Todos os colaboradores da filial, para permitir filtrar mesmo quem não tem agenda.
        boardEmployees.forEach((employee) => {
            if (!employee?.id || uniqueEmployees.has(employee.id)) {
                return;
            }

            uniqueEmployees.set(employee.id, buildQuickOption(
                employee.id,
                getDisplayName(employee, 'Colaborador'),
                { employee }
            ));
        });

        // Fallback: colaboradores presentes apenas nos agendamentos (ex.: inativados com
        // atendimentos no período). Quem não presta serviço fica de fora mesmo assim.
        filteredAppointments.forEach((event) => {
            const employee = event.resource?.employee;

            if (!employee?.id || uniqueEmployees.has(employee.id) || !isServiceProvider(employee)) {
                return;
            }

            uniqueEmployees.set(employee.id, buildQuickOption(
                employee.id,
                getDisplayName(employee, 'Colaborador'),
                { employee }
            ));
        });

        return Array.from(uniqueEmployees.values()).sort((leftOption, rightOption) => leftOption.label.localeCompare(rightOption.label));
    }, [boardEmployees, filteredAppointments]);

    const collaboratorScopedAppointments = useMemo(() => {
        if (!selectedCollaboratorFilter?.value) {
            return filteredAppointments;
        }

        return filteredAppointments.filter((event) => String(event.resource?.employee?.id || '') === String(selectedCollaboratorFilter.value));
    }, [filteredAppointments, selectedCollaboratorFilter]);

    /**
     * Agendamentos efetivamente desenhados na agenda.
     *
     * O cancelado sai por padrão: o horário voltou a ficar livre, mas o cartão
     * continuava disputando a coluna do colaborador e espremia o agendamento que
     * ocupou o lugar dele, dando a impressão de dois atendimentos no mesmo horário.
     * Ele continua contando nos indicadores do dia e volta à tela pelo botão
     * "Cancelados" quando alguém precisa conferir o que foi desmarcado.
     */
    const visibleAppointments = useMemo(() => {
        if (showCanceledAppointments) {
            return collaboratorScopedAppointments;
        }

        return collaboratorScopedAppointments.filter((event) => event.status !== 'CANCELED');
    }, [collaboratorScopedAppointments, showCanceledAppointments]);

    // Índice dia da semana → expedientes daquele dia, para consultar a janela de cada data sem
    // varrer a lista. É uma lista por dia, e não um registro só, porque sem filial selecionada
    // a agenda carrega o expediente de várias filiais e cada uma tem o seu horário na
    // segunda-feira; guardando um por dia, a última filial lida apagava as anteriores.
    const workingHoursByDay = useMemo(() => {
        const index = {};

        branchWorkingHours.forEach((workingHours) => {
            if (!workingHours?.dayOfWeek) {
                return;
            }

            if (!index[workingHours.dayOfWeek]) {
                index[workingHours.dayOfWeek] = [];
            }

            index[workingHours.dayOfWeek].push(workingHours);
        });

        return index;
    }, [branchWorkingHours]);

    // Expediente de cada filial na data. A data especial substitui o horário semanal só das
    // filiais dela: com "todas as filiais", o feriado de uma unidade não fecha a agenda das outras.
    const workingHoursOn = useCallback((date) => {
        const weekly = workingHoursByDay[JS_DAY_TO_ENUM[date.getDay()]] || [];
        const specials = specialDaysOn(specialDays, date);

        if (specials.length === 0) {
            return weekly;
        }

        const replacedBranches = new Set(specials.flatMap((day) => day.branchIds || []));
        return [
            ...weekly.filter((workingHours) => !replacedBranches.has(workingHours.branchId)),
            ...specials.map(specialDayAsWorkingHours)
        ];
    }, [workingHoursByDay, specialDays]);

    // Menor janela (em minutos) que cobre o expediente de todas as datas informadas.
    // Retorna null quando nenhuma delas tem horário cadastrado e aberto — aí o chamador
    // mantém o comportamento anterior de exibir o dia inteiro.
    const getWorkingWindow = useCallback((dates) => {
        let openMinutes = null;
        let closeMinutes = null;

        dates.forEach((date) => {
            const workingHoursOfDay = workingHoursOn(date);

            workingHoursOfDay.forEach((workingHours) => {
                if (!workingHours?.isOpen) {
                    return;
                }

                const dayOpen = toMinutesOfDay(workingHours.openTime);
                const dayClose = toMinutesOfDay(workingHours.closeTime);

                if (dayOpen === null || dayClose === null || dayClose <= dayOpen) {
                    return;
                }

                openMinutes = openMinutes === null ? dayOpen : Math.min(openMinutes, dayOpen);
                closeMinutes = closeMinutes === null ? dayClose : Math.max(closeMinutes, dayClose);
            });
        });

        if (openMinutes === null || closeMinutes === null) {
            return null;
        }

        return { openMinutes: floorToHour(openMinutes), closeMinutes: ceilToHour(closeMinutes) };
    }, [workingHoursOn]);

    /**
     * A filial está fechada na data (ou todas as filiais exibidas estão, quando o seletor
     * principal está em "todas").
     *
     * Só afirma "fechado" quando existe expediente cadastrado para aquele dia da semana e
     * nenhum registro está aberto. Sem cadastro nenhum não dá para afirmar nada: a agenda
     * segue liberada, como era antes de o expediente existir.
     */
    const isBranchClosedOn = useCallback((date) => {
        const workingHoursOfDay = workingHoursOn(date);

        if (!workingHoursOfDay || workingHoursOfDay.length === 0) {
            return false;
        }

        return !workingHoursOfDay.some((workingHours) => {
            if (!workingHours?.isOpen) {
                return false;
            }

            const dayOpen = toMinutesOfDay(workingHours.openTime);
            const dayClose = toMinutesOfDay(workingHours.closeTime);

            return dayOpen !== null && dayClose !== null && dayClose > dayOpen;
        });
    }, [workingHoursOn]);

    // Recorta a grade do react-big-calendar ao expediente da filial nos dias visíveis.
    // A janela é alargada quando existe agendamento fora do expediente (remarcação manual,
    // horário antigo), porque min/max no calendário escondem o evento em vez de rolá-lo.
    const calendarTimeRange = useMemo(() => {
        const visibleDays = calendarView === 'day' ? [currentDate] : getWeekDays(currentDate);
        const window = getWorkingWindow(visibleDays);

        if (!window) {
            return { min: undefined, max: undefined };
        }

        let { openMinutes, closeMinutes } = window;

        collaboratorScopedAppointments.forEach((event) => {
            if (!event.start || !event.end) {
                return;
            }

            openMinutes = Math.min(openMinutes, floorToHour(dateToMinutesOfDay(event.start)));
            closeMinutes = Math.max(closeMinutes, ceilToHour(dateToMinutesOfDay(event.end)));
        });

        const minDate = new Date(currentDate);
        minDate.setHours(Math.floor(openMinutes / 60), openMinutes % 60, 0, 0);

        const maxDate = new Date(currentDate);
        // 24:00 não existe como hora do dia; o fim do expediente vira 23:59 nesse caso.
        if (closeMinutes >= DEFAULT_BOARD_END_MINUTES) {
            maxDate.setHours(23, 59, 59, 999);
        } else {
            maxDate.setHours(Math.floor(closeMinutes / 60), closeMinutes % 60, 0, 0);
        }

        return { min: minDate, max: maxDate };
    }, [calendarView, currentDate, getWorkingWindow, collaboratorScopedAppointments]);

    useEffect(() => {
        // As opções chegam após a consulta: restaura o filtro com o objeto real da lista.
        if (initialCollaboratorRef.current) {
            const initialOption = collaboratorFilterOptions.find((option) => String(option.value) === initialCollaboratorRef.current);
            if (initialOption) {
                initialCollaboratorRef.current = null;
                setSelectedCollaboratorFilter(initialOption);
                return;
            }
        }
        setSelectedCollaboratorFilter((currentFilter) => {
            if (!currentFilter) {
                return null;
            }

            return collaboratorFilterOptions.some((option) => option.value === currentFilter.value)
                ? currentFilter
                : null;
        });
    }, [collaboratorFilterOptions]);

    useEffect(() => {
        if (!selectedAppointment?.id) {
            return;
        }

        const stillVisible = collaboratorScopedAppointments.some((event) => event.resource?.id === selectedAppointment.id);

        if (!stillVisible) {
            selectedAppointmentRef.current = null;
            setSelectedAppointment(null);
        }
    }, [collaboratorScopedAppointments, selectedAppointment]);

    /**
     * Abre o cadastro de cliente a partir do agendamento rápido, escondendo o painel.
     *
     * O painel não é um Modal do react-bootstrap: tem fundo escuro e camada próprios, e o
     * cadastro abria por trás deles, coberto pela opacidade. Aqui o painel só sai da tela,
     * sem passar por closeSlotPlanner, então serviços, colaborador, data e observação
     * continuam preenchidos para quando ele voltar.
     */
    const openQuickCustomerFromPlanner = () => {
        plannerHiddenForCustomerRef.current = true;
        setSlotPlannerOpen(false);
        setQuickCustomerOpen(true);
    };

    /**
     * Traz o agendamento rápido de volta depois que o cadastro de cliente saiu da tela.
     *
     * Espera o fim da animação de saída de propósito: ao terminar, o Modal devolve ao body o
     * overflow que havia quando abriu. Reabrir o painel antes disso faria essa restauração
     * apagar a trava de rolagem que o painel acabou de aplicar.
     */
    const restorePlannerAfterQuickCustomer = () => {
        if (!plannerHiddenForCustomerRef.current) return;
        plannerHiddenForCustomerRef.current = false;
        setSlotPlannerOpen(true);
    };

    const closeSlotPlanner = () => {
        setSlotPlannerOpen(false);
        setEncaixeMode(false);
        setSlotPlannerError('');
        setPlannerSlots([]);
        setReschedulingAppointment(null);
        setEditingAppointment(null);
        setServicesOnlyAppointment(false);
        setEncaixeReference(null);
        setEncaixeTime('');
        setUseSpecificQuickTime(false);
        setSpecificQuickStartTime('');
        setSpecificQuickEndTime('');
        setPlannerCustomer(null);
        setPlannerServices([]);
        setPlannerDeposit(buildDepositDraft());
        setPlannerNotes('');
        // Colaborador e filial também precisam sair. Enquanto ficavam, a próxima abertura
        // pelo botão herdava os da vez anterior: horários do expediente de outra unidade e,
        // pior, risco de gravar o agendamento no profissional errado sem ninguém notar.
        setPlannerEmployee(null);
        setPlannerBranch(null);
    };

    /**
     * Busca o agendamento por id para ter a lista real de serviços da visita.
     *
     * A listagem do mês devolve só serviceNames (nomes, para o rótulo do card), então o objeto
     * que vem do card não serve para preencher a seleção de serviços. Isso vale para reagendar
     * e para editar: os dois enviam serviceItems no salvamento, e o backend passou a reconciliar
     * a lista, então mandar só o serviço primário apagaria os demais.
     */
    const fetchAppointmentWithServices = async (apt) => {
        try {
            const response = await axiosInstance.get(`${Api.appointment.appointment}/${apt.id}`);
            return response.data || apt;
        } catch (error) {
            // Seguir com o objeto do card é melhor que travar a ação; o operador confere a
            // seleção na tela antes de salvar.
            console.error('Erro ao carregar os serviços do agendamento:', error);
            return apt;
        }
    };

    /** Serviços do agendamento como opções do select, com o primário como reserva. */
    const buildPlannerServiceOptions = (apt) => {
        const items = Array.isArray(apt.serviceItems) ? apt.serviceItems.filter((item) => item?.service?.id) : [];

        if (items.length > 0) {
            return items.map((item) => ({
                value: item.service.id,
                label: getServiceLabel(item.service),
                service: item.service
            }));
        }

        // Agendamento antigo, gravado antes dos itens existirem.
        return apt.service
            ? [{ value: apt.service.id, label: getServiceLabel(apt.service), service: apt.service }]
            : [];
    };

    const handleRescheduleRequest = async (apt) => {
        const detailed = await fetchAppointmentWithServices(apt);

        setReschedulingAppointment(detailed);

        if (detailed.customer) {
            setPlannerCustomer({ value: detailed.customer.id, label: getDisplayName(detailed.customer), customer: detailed.customer });
        }

        if (detailed.branch) {
            setPlannerBranch({ value: detailed.branch.id, label: detailed.branch.name, branch: detailed.branch });
        } else if (detailed.customer?.branch) {
            setPlannerBranch({ value: detailed.customer.branch.id, label: detailed.customer.branch.name, branch: detailed.customer.branch });
        } else if (selectedBranch) {
            setPlannerBranch({ value: selectedBranch.id, label: selectedBranch.name, branch: selectedBranch });
        }

        if (detailed.employee) {
            setPlannerEmployee({ value: detailed.employee.id, label: getDisplayName(detailed.employee), employee: detailed.employee });
        }

        setPlannerServices(buildPlannerServiceOptions(detailed));

        openSlotPlanner(detailed.scheduledDate);
        setSelectedAppointment(null);
    };

    /**
     * Abre o painel em modo de correção, com tudo do atendimento pré-carregado.
     *
     * Os serviços vêm de serviceItems, que é a lista real da visita; o campo service é só o
     * primário e serve de reserva para agendamento antigo, gravado antes dos itens existirem.
     */
    const handleEditRequest = async (apt) => {
        const detailed = await fetchAppointmentWithServices(apt);

        setEditingAppointment(detailed);
        setServicesOnlyAppointment(false);

        if (detailed.customer) {
            setPlannerCustomer({ value: detailed.customer.id, label: getDisplayName(detailed.customer), customer: detailed.customer });
        }

        if (detailed.branch) {
            setPlannerBranch({ value: detailed.branch.id, label: detailed.branch.name, branch: detailed.branch });
        }

        if (detailed.employee) {
            setPlannerEmployee({ value: detailed.employee.id, label: getDisplayName(detailed.employee), employee: detailed.employee });
        }

        setPlannerServices(buildPlannerServiceOptions(detailed));
        setPlannerNotes(detailed.notes || '');
        openSlotPlanner(detailed.scheduledDate);
        setSelectedAppointment(null);
    };

    /** Abre o seletor no próprio card, preservando data, horário e duração reservada. */
    const handleAddServicesRequest = async (apt) => {
        const detailed = await fetchAppointmentWithServices(apt);
        setEditingAppointment(detailed);
        setServicesOnlyAppointment(true);
        if (detailed.customer) {
            setPlannerCustomer({ value: detailed.customer.id, label: getDisplayName(detailed.customer), customer: detailed.customer });
        }
        if (detailed.branch) {
            setPlannerBranch({ value: detailed.branch.id, label: detailed.branch.name, branch: detailed.branch });
        }
        if (detailed.employee) {
            setPlannerEmployee({ value: detailed.employee.id, label: getDisplayName(detailed.employee), employee: detailed.employee });
        }
        setPlannerServices(buildPlannerServiceOptions(detailed));
        setPlannerNotes(detailed.notes || '');
        openSlotPlanner(detailed.scheduledDate);
        setSelectedAppointment(null);
    };


    /**
     * Salva a correção mantendo o horário original. Trocar horário é o Reagendar, que é outra
     * ação e outra conversa com o cliente.
     */
    const handleSaveAppointmentEdit = () => {
        if (!editingAppointment) return;

        if (!plannerCustomer?.value || !plannerBranch?.value || !plannerEmployee?.value || plannerServices.length === 0) {
            warnAlert('Cliente, filial, profissional e ao menos um serviço são obrigatórios.');
            return;
        }

        if (servicesOnlyAppointment) {
            const serviceItems = plannerServices.map((option) => ({
                service: { id: option.value },
                price: option.service?.price != null ? Number(option.service.price) : null
            }));

            setSlotPlannerSubmitting(true);
            axiosInstance.patch(`${Api.appointment.appointment}/${editingAppointment.id}/services`, {
                serviceItems,
                price: plannerTotalPrice || editingAppointment.price || null
            })
                .then(() => {
                    successAlert('Serviços atualizados sem alterar o horário.');
                    closeSlotPlanner();
                    refreshAppointments();
                })
                .catch((error) => errorObjectAlert(error))
                .finally(() => setSlotPlannerSubmitting(false));
            return;
        }

        const primaryService = plannerServices[0];
        const payload = {
            uuid: editingAppointment.uuid,
            customer: { id: plannerCustomer.value },
            employee: { id: plannerEmployee.value },
            service: { id: primaryService.value },
            serviceItems: plannerServices.map((option) => ({
                service: { id: option.value },
                price: option.service?.price != null ? Number(option.service.price) : null
            })),
            appointmentStatus: editingAppointment.appointmentStatus,
            // Horário preservado exatamente como está.
            scheduledDate: toLocalISOString(new Date(editingAppointment.scheduledDate)),
            duration: resolveDuration(plannerTotalDuration, editingAppointment.duration),
            price: plannerTotalPrice || editingAppointment.price || null,
            notes: plannerNotes || null,
            branch: { id: plannerBranch.value },
            cancellationReason: editingAppointment.cancellationReason || null
        };

        setSlotPlannerSubmitting(true);

        axiosInstance.patch(`${Api.appointment.appointment}/${editingAppointment.id}`, payload)
            .then(() => {
                successAlert('Agendamento atualizado.');
                closeSlotPlanner();
                refreshAppointments();
            })
            .catch((error) => errorObjectAlert(error))
            .finally(() => {
                setSlotPlannerSubmitting(false);
            });
    };

    /**
     * Abre o painel para encaixar um cliente sobre um horário já ocupado.
     *
     * Aproveita o horário, o profissional e a filial do atendimento existente. Cliente e
     * serviços ficam vazios de propósito: o encaixe é de outra pessoa. A lista de horários
     * livres não aparece porque o horário desejado é, por definição, um que já está tomado.
     */
    const handleEncaixeRequest = (apt) => {
        setEncaixeReference(apt);

        if (apt.branch) {
            setPlannerBranch({ value: apt.branch.id, label: apt.branch.name, branch: apt.branch });
        }

        if (apt.employee) {
            setPlannerEmployee({ value: apt.employee.id, label: getDisplayName(apt.employee), employee: apt.employee });
        }

        setPlannerCustomer(null);
        setPlannerServices([]);
        setEncaixeTime(moment(apt.scheduledDate).format('HH:mm'));
        openSlotPlanner(apt.scheduledDate, { encaixe: true });
        setSelectedAppointment(null);
    };

    // O encaixe parte do bloqueio sem removê-lo: só a criação com encaixe marcado
    // autoriza a exceção, enquanto o restante do período continua protegido.
    const handleEncaixeBlockRequest = (block, employee) => {
        const branchId = block.branchId ?? selectedBranch?.id
            ?? (employee.branches?.length === 1 ? employee.branches[0].id : null);
        const branch = plannerBranchOptions.find((option) => String(option.value) === String(branchId))
            || (selectedBranch?.id != null && String(selectedBranch.id) === String(branchId)
                ? { value: selectedBranch.id, label: selectedBranch.name, branch: selectedBranch }
                : null);
        setEncaixeReference(null);
        setEncaixeTime(moment(block.startDate).format('HH:mm'));
        setPlannerCustomer(null);
        setPlannerServices([]);
        setPlannerNotes('');
        setPlannerEmployee({ value: employee.id, label: getDisplayName(employee), employee });
        setPlannerBranch(branch);
        openSlotPlanner(block.startDate, { encaixe: true });
    };

    const openEncaixePlanner = () => {
        setEncaixeReference(null);
        setEncaixeTime('');
        setPlannerCustomer(null);
        setPlannerServices([]);
        setPlannerNotes('');
        setPlannerEmployee(selectedCollaboratorFilter || null);
        setPlannerBranch(selectedBranch
            ? { value: selectedBranch.id, label: selectedBranch.name, branch: selectedBranch }
            : resolveDefaultPlannerBranch());
        openSlotPlanner(collaboratorDate, { encaixe: true });
    };

    /** Cria o encaixe no horário informado, assumindo a sobreposição. */
    const handleCreateEncaixe = () => {
        if (!plannerCustomer?.value || !plannerBranch?.value || !plannerEmployee?.value || plannerServices.length === 0) {
            warnAlert('Cliente, filial, profissional e ao menos um serviço são obrigatórios.');
            return;
        }

        if (!isValidTimeValue(encaixeTime)) {
            warnAlert('Informe um horário válido para o encaixe.');
            return;
        }

        if (!validatePlannerDeposit()) {
            return;
        }

        const [hours, minutes] = encaixeTime.split(':').map(Number);
        const scheduledDate = moment(slotPlannerDate || encaixeReference?.scheduledDate || collaboratorDate)
            .startOf('day')
            .hours(hours)
            .minutes(minutes)
            .toDate();

        const primaryService = plannerServices[0];
        const payload = {
            uuid: generateUUID(),
            customer: { id: plannerCustomer.value },
            employee: { id: plannerEmployee.value },
            service: { id: primaryService.value },
            serviceItems: plannerServices.map((option) => ({
                service: { id: option.value },
                price: option.service?.price != null ? Number(option.service.price) : null
            })),
            appointmentStatus: 'SCHEDULED',
            scheduledDate: toLocalISOString(scheduledDate),
            duration: resolveDuration(plannerTotalDuration),
            price: plannerTotalPrice || null,
            notes: plannerNotes || null,
            // A recepção assume a sobreposição: o backend não bloqueia o conflito com encaixe.
            encaixe: true,
            branch: { id: plannerBranch.value }
        };

        warnAlertYesOrNo(
            `Confirmar encaixe de ${plannerCustomer.label || 'cliente selecionado'} com ${plannerEmployee.label} em ${moment(scheduledDate).format('DD/MM/YYYY [às] HH:mm')}?`,
            () => {
                setSlotPlannerSubmitting(true);
                axiosInstance.post(Api.appointment.appointment, payload)
                    .then(async (response) => {
                        const depositProblem = await registerPlannerDeposit(response.data);
                        successAlert(`Encaixe criado para ${moment(scheduledDate).format('DD/MM/YYYY [às] HH:mm')}.`);
                        if (depositProblem) {
                            warnAlert(`A reserva não foi registrada: ${depositProblem}. Receba pelo detalhe do agendamento.`);
                        }
                        closeSlotPlanner();
                        setCurrentDate(scheduledDate);
                        setCollaboratorDate(scheduledDate);
                        refreshAppointments();
                    })
                    .catch((error) => errorObjectAlert(error))
                    .finally(() => {
                        setSlotPlannerSubmitting(false);
                    });
            }
        );
    };

    /**
     * Valida a reserva antes de criar o agendamento. Só vale na criação: edição e reagendamento
     * não recebem reserva por aqui (ela se recebe e devolve pelo detalhe do agendamento).
     */
    const validatePlannerDeposit = () => {
        if (editingAppointment || reschedulingAppointment || !requiresDeposit(plannerServices)) {
            return true;
        }
        const problem = validateDepositDraft(plannerDeposit, plannerTotalPrice);
        if (problem) {
            warnAlert(problem);
            return false;
        }
        return true;
    };

    /**
     * Registra a reserva logo depois de o agendamento ser criado. Se falhar, o agendamento
     * continua valendo e a recepção é avisada para receber pelo detalhe, em vez de perder o
     * horário que acabou de marcar.
     */
    const registerPlannerDeposit = (createdAppointment) => {
        if (!plannerDeposit.receiveNow || !requiresDeposit(plannerServices) || !createdAppointment?.id) {
            return Promise.resolve(null);
        }
        return registerDeposit(createdAppointment.id, plannerDeposit)
            .then(() => null)
            .catch((error) => error.response?.data?.message || error.message);
    };

    const handleSelectSlot = (slotInfo) => {
        if (!slotInfo?.start) return;

        // Mesma recusa do quadro: dia fechado não aceita agendamento.
        if (isBranchClosedOn(slotInfo.start)) {
            warnAlert('A filial está fechada neste dia. Escolha outra data para agendar.');
            return;
        }

        openSlotPlanner(slotInfo.start);
    };

    const handleQuickAppointmentCreate = (slot, requestedStartTime = null, requestedEndTime = null) => {
        if (!plannerCustomer?.value) {
            warnAlert('Selecione o cliente antes de confirmar o horário.');
            return;
        }

        if (!plannerBranch?.value || !plannerEmployee?.value || plannerServices.length === 0 || !slotPlannerDate) {
            warnAlert('Preencha filial, profissional e ao menos um serviço para continuar.');
            return;
        }

        const slotTime = requestedStartTime || formatSlotTime(slot);
        if (!slotTime) {
            warnAlert('Não foi possível identificar o horário selecionado.');
            return;
        }

        const scheduledDate = slot?.startTime ? new Date(slot.startTime) : new Date(slotPlannerDate);
        if (requestedStartTime || !slot?.startTime) {
            const [hours, minutes] = slotTime.split(':').map(Number);
            scheduledDate.setHours(hours || 0, minutes || 0, 0, 0);
        }

        let specificDuration = null;
        if (requestedStartTime) {
            if (!requestedEndTime) {
                warnAlert('Informe o horário de fim.');
                return;
            }

            const [endHours, endMinutes] = requestedEndTime.split(':').map(Number);
            const endDate = new Date(scheduledDate);
            endDate.setHours(endHours || 0, endMinutes || 0, 0, 0);
            specificDuration = Math.round((endDate - scheduledDate) / 60000);

            if (specificDuration <= 0) {
                warnAlert('O horário de fim deve ser posterior ao horário de início.');
                return;
            }

            // No agendamento rápido a faixa informada é a reserva real. Não deixamos
            // salvar uma janela menor que os serviços, pois isso criaria o indicador de
            // exceção de duração imediatamente após um cadastro aparentemente válido.
            if (specificDuration < plannerTotalDuration) {
                warnAlert(`O horário informado tem ${specificDuration} minutos, mas os serviços selecionados precisam de ${plannerTotalDuration} minutos.`);
                return;
            }
        }

        let slotDuration = null;
        if (slot?.startTime && slot?.endTime) {
            slotDuration = Math.round((new Date(slot.endTime) - new Date(slot.startTime)) / 60000);
        }

        // Algumas respostas antigas de disponibilidade trazem a janela sem fim válido.
        // Nesse caso, zero não pode sobrescrever a duração real dos serviços e criar a
        // exceção de duração logo após um cadastro normal.
        const appointmentDuration = specificDuration ?? (slotDuration > 0 ? slotDuration : plannerTotalDuration);

        if (!validatePlannerDeposit()) {
            return;
        }

        // O primeiro serviço é o primário (compatibilidade com quem lê um único serviço); todos vão em serviceItems.
        const primaryService = plannerServices[0];

        const payload = {
            uuid: reschedulingAppointment ? reschedulingAppointment.uuid : generateUUID(),
            customer: { id: plannerCustomer.value },
            employee: { id: plannerEmployee.value },
            service: { id: primaryService.value },
            serviceItems: plannerServices.map((option) => ({
                service: { id: option.value },
                price: option.service?.price != null ? Number(option.service.price) : null
            })),
            appointmentStatus: reschedulingAppointment ? reschedulingAppointment.appointmentStatus : 'SCHEDULED',
            scheduledDate: toLocalISOString(scheduledDate),
            duration: resolveDuration(appointmentDuration, plannerTotalDuration,
                reschedulingAppointment?.duration, reschedulingAppointment?.durationMinutes),
            price: plannerTotalPrice || (reschedulingAppointment ? reschedulingAppointment.price : null) || null,
            notes: reschedulingAppointment ? reschedulingAppointment.notes : null,
            branch: { id: plannerBranch.value },
            cancellationReason: reschedulingAppointment ? reschedulingAppointment.cancellationReason : null
        };

        setSlotPlannerSubmitting(true);
        
        const request = reschedulingAppointment 
            ? axiosInstance.patch(`${Api.appointment.appointment}/${reschedulingAppointment.id}`, payload)
            : axiosInstance.post(Api.appointment.appointment, payload);

        request
            .then(async (response) => {
                // Reagendamento (PATCH) não devolve o agendamento e não recebe reserva por aqui.
                const depositProblem = reschedulingAppointment ? null : await registerPlannerDeposit(response.data);
                successAlert(reschedulingAppointment 
                    ? `Agendamento reagendado para ${moment(scheduledDate).format('DD/MM/YYYY [às] HH:mm')}.` 
                    : `Agendamento criado para ${moment(scheduledDate).format('DD/MM/YYYY [às] HH:mm')}.`
                );
                if (depositProblem) {
                    warnAlert(`A reserva não foi registrada: ${depositProblem}. Receba pelo detalhe do agendamento.`);
                }
                closeSlotPlanner();
                setCurrentDate(scheduledDate);
                setCollaboratorDate(scheduledDate);
                refreshAppointments();
            })
            .catch((error) => errorObjectAlert(error))
            .finally(() => {
                setSlotPlannerSubmitting(false);
            });
    };

    const collaboratorData = useMemo(() => {
        const dayStr = moment(collaboratorDate).format('YYYY-MM-DD');
        const dayApts = visibleAppointments.filter(evt =>
            moment(evt.start).format('YYYY-MM-DD') === dayStr
        );
        const empMap = {};
        // Semeia o board com todos os colaboradores da filial (ou apenas o filtrado),
        // para exibir uma coluna por colaborador mesmo sem agendamento no dia.
        const seedEmployees = selectedCollaboratorFilter?.value
            ? boardEmployees.filter(emp => String(emp?.id) === String(selectedCollaboratorFilter.value))
            : boardEmployees;
        seedEmployees.forEach(emp => {
            if (!emp?.id) return;
            empMap[emp.id] = { employee: emp, appointments: [] };
        });
        dayApts.forEach(evt => {
            const emp = evt.resource?.employee;
            // Sem coluna para quem não presta serviço, nem quando há agendamento no dia: a
            // agenda mostra só prestadores, por decisão do produto.
            if (!emp?.id || !isServiceProvider(emp)) return;
            if (!empMap[emp.id]) {
                empMap[emp.id] = { employee: emp, appointments: [] };
            }
            empMap[emp.id].appointments.push(evt);
        });
        Object.values(empMap).forEach(ed => {
            ed.appointments.sort((a, b) => a.start - b.start);
            // Folga do colaborador no dia exibido. A coluna continua no quadro, porque o
            // operador conta as colunas pela posição, mas fica marcada e sem clique para
            // criar: o servidor recusaria o agendamento de qualquer jeito.
            ed.isDayOff = !worksOnDate(ed.employee?.workDays, new Date(collaboratorDate));
            // Férias fecham a coluna do mesmo jeito que a folga, mas com outro motivo na
            // tela: a recepção precisa saber que é férias e quando a pessoa volta.
            ed.isOnVacation = isOnVacationOn(ed.employee, collaboratorDate);
            const completedCount = ed.appointments.filter((apt) => apt.status === 'COMPLETED').length;
            const activeCount = ed.appointments.filter((apt) => ['CONFIRMED', 'IN_PROGRESS'].includes(apt.status)).length;
            // Mesmo recorte da receita prevista do dia: cancelado e falta não entram na
            // conta, senão o número do colaborador oscilaria ao exibir os cancelados.
            const projectedRevenue = ed.appointments
                .filter((apt) => apt.status !== 'CANCELED' && apt.status !== 'NO_SHOW')
                .reduce((total, apt) => total + (getAppointmentPriceValue(apt.resource) || 0), 0);
            ed.summary = {
                completedCount,
                activeCount,
                projectedRevenue,
                firstTime: ed.appointments[0] ? moment(ed.appointments[0].start).format('HH:mm') : '--:--',
                lastTime: ed.appointments[ed.appointments.length - 1] ? moment(ed.appointments[ed.appointments.length - 1].end).format('HH:mm') : '--:--'
            };
        });

        // Ordem alfabética fixa, sempre. A ordenação anterior colocava na frente quem tinha
        // mais agendamentos (e usava o horário do primeiro atendimento como desempate), então
        // cada agendamento novo mudava a contagem e fazia a coluna do colaborador pular de
        // lugar no meio do expediente. O operador decora a posição da coluna, então ela
        // precisa ser estável ao longo do dia, independente do movimento de cada um.
        return Object.values(empMap).sort((leftEmployee, rightEmployee) => getDisplayName(leftEmployee.employee)
            .localeCompare(getDisplayName(rightEmployee.employee), 'pt-BR', { sensitivity: 'base' }));
    }, [visibleAppointments, collaboratorDate, boardEmployees, selectedCollaboratorFilter]);

    const collaboratorBoardConfig = useMemo(() => {
        // Base da grade: o expediente do dia exibido. Sem expediente cadastrado, o dia inteiro.
        const workingWindow = getWorkingWindow([collaboratorDate]);
        const baseStart = workingWindow ? workingWindow.openMinutes : DEFAULT_BOARD_START_MINUTES;
        const baseEnd = workingWindow ? workingWindow.closeMinutes : DEFAULT_BOARD_END_MINUTES;

        const buildBoard = (startMinutes, endMinutes) => {
            const slots = [];
            for (let minutes = startMinutes; minutes <= endMinutes; minutes += COLLABORATOR_BOARD_INTERVAL_MINUTES) {
                slots.push(minutes);
            }

            return {
                startMinutes,
                endMinutes,
                slots,
                totalHeight: ((endMinutes - startMinutes) / COLLABORATOR_BOARD_INTERVAL_MINUTES) * COLLABORATOR_BOARD_SLOT_HEIGHT
            };
        };

        if (collaboratorData.length === 0) {
            return buildBoard(baseStart, baseEnd);
        }

        const allMoments = collaboratorData.flatMap((employeeData) => employeeData.appointments.flatMap((appointment) => [
            moment(appointment.start),
            moment(appointment.end)
        ]));

        const minMinutes = Math.min(...allMoments.map((item) => item.hours() * 60 + item.minutes()));
        const maxMinutes = Math.max(...allMoments.map((item) => item.hours() * 60 + item.minutes()));
        // Agendamento fora do expediente ainda precisa aparecer: a janela estica para caber nele.
        const roundedStart = Math.min(
            baseStart,
            Math.floor(minMinutes / COLLABORATOR_BOARD_INTERVAL_MINUTES) * COLLABORATOR_BOARD_INTERVAL_MINUTES
        );
        const roundedEnd = Math.max(
            baseEnd,
            Math.max(
                roundedStart + 60,
                Math.ceil(maxMinutes / COLLABORATOR_BOARD_INTERVAL_MINUTES) * COLLABORATOR_BOARD_INTERVAL_MINUTES + COLLABORATOR_BOARD_INTERVAL_MINUTES
            )
        );

        return buildBoard(roundedStart, roundedEnd);
    }, [collaboratorData, collaboratorDate, getWorkingWindow]);

    // Dia sem expediente no quadro por colaborador: o servidor recusa qualquer agendamento
    // nele (AppointmentWorkingHoursValidator) e não devolve horário livre nenhum, então a tela
    // precisa dizer isso antes de o operador preencher o formulário inteiro.
    const isCollaboratorDayClosed = useMemo(
        () => isBranchClosedOn(collaboratorDate),
        [isBranchClosedOn, collaboratorDate]
    );

    // Datas especiais do dia no quadro: o aviso diz o motivo do fechamento (feriado) e, no dia
    // de horário especial, a faixa reduzida, para a recepção não estranhar a grade mais curta.
    const collaboratorSpecialDays = useMemo(
        () => specialDaysOn(specialDays, collaboratorDate),
        [specialDays, collaboratorDate]
    );
    const collaboratorClosedReason = collaboratorSpecialDays.find((day) => !day.isOpen)?.reason || null;
    const collaboratorSpecialHours = collaboratorSpecialDays.filter((day) => day.isOpen);
    // Com "todas as filiais" o texto diz de qual unidade é cada faixa.
    const collaboratorSpecialHoursText = collaboratorSpecialHours
        .map((day) => `${selectedBranch?.id || !day.branchNames?.length ? '' : `${day.branchNames.join(', ')}: `}${specialDayHoursLabel(day)} (${day.reason})`)
        .join('; ');

    const isCollaboratorToday = useMemo(
        () => moment(collaboratorDate).isSame(new Date(), 'day'),
        [collaboratorDate]
    );

    const collaboratorCurrentTimeTop = useMemo(() => {
        if (!isCollaboratorToday) {
            return null;
        }

        const now = moment();
        const currentMinutes = now.hours() * 60 + now.minutes();
        const relativeMinutes = Math.max(currentMinutes - collaboratorBoardConfig.startMinutes, 0);
        const top = (relativeMinutes / COLLABORATOR_BOARD_INTERVAL_MINUTES) * COLLABORATOR_BOARD_SLOT_HEIGHT;

        return Math.min(top, collaboratorBoardConfig.totalHeight);
    }, [collaboratorBoardConfig.startMinutes, collaboratorBoardConfig.totalHeight, isCollaboratorToday]);

    /** Guarda onde o operador está olhando, para sobreviver ao próximo recarregamento. */
    const rememberBoardScroll = useCallback((event) => {
        boardScrollMemoryRef.current = {
            top: event.currentTarget.scrollTop,
            left: event.currentTarget.scrollLeft
        };
    }, []);

    /*
     * Decide onde o quadro fica posicionado depois de cada carregamento.
     *
     * Troca de data (ou primeira carga): posiciona na altura do horário atual.
     * Recarregamento dos mesmos dados: devolve exatamente onde o operador estava.
     *
     * A distinção existe porque todo salvamento dispara um refresh, e durante ele o quadro
     * dá lugar ao spinner: o elemento é recriado com a rolagem zerada. Sem restaurar,
     * ajustar a duração de um atendimento das 18h jogava o operador de volta para o começo
     * do dia, longe do cartão que ele acabara de mexer.
     */
    useEffect(() => {
        if (viewMode !== 'collaborator' || showLoading || !collaboratorBoardShellRef.current) {
            return undefined;
        }

        const dateKey = moment(collaboratorDate).format('YYYY-MM-DD');
        const isNewDate = boardScrollAnchorRef.current !== dateKey;
        boardScrollAnchorRef.current = dateKey;

        // A rolagem vertical vive no shell, junto com a horizontal.
        const boardElement = collaboratorBoardShellRef.current;

        const syncScrollPosition = () => {
            if (!isNewDate) {
                const remembered = boardScrollMemoryRef.current;
                boardElement.scrollTop = remembered.top;
                boardElement.scrollLeft = remembered.left;
                return;
            }

            // Data nova começa da esquerda: o primeiro colaborador é a referência.
            boardElement.scrollLeft = 0;

            const currentTop = isCollaboratorToday
                ? Math.max((collaboratorCurrentTimeTop || 0) - boardElement.clientHeight * 0.28, 0)
                : 0;

            boardElement.scrollTop = currentTop;
            boardScrollMemoryRef.current = { top: currentTop, left: 0 };
        };

        const frameId = window.requestAnimationFrame(syncScrollPosition);

        return () => window.cancelAnimationFrame(frameId);
    }, [collaboratorCurrentTimeTop, collaboratorDate, isCollaboratorToday, showLoading, viewMode]);
    const appointmentLayoutByEmployee = useMemo(() => {
        const layoutByEmployee = {};

        collaboratorData.forEach((employeeData) => {
            const items = employeeData.appointments
                .map((appointment) => ({
                    key: getAppointmentLayoutKey(appointment),
                    start: moment(appointment.start).valueOf(),
                    end: moment(appointment.end).valueOf(),
                    lane: 0,
                    columnCount: 1
                }))
                .sort((left, right) => left.start - right.start || left.end - right.end);

            const employeeLayout = {};
            let activeItems = [];
            let clusterItems = [];
            let clusterColumnCount = 1;

            const flushCluster = () => {
                if (clusterItems.length === 0) return;

                clusterItems.forEach((item) => {
                    employeeLayout[item.key] = {
                        lane: item.lane,
                        columnCount: clusterColumnCount
                    };
                });

                clusterItems = [];
                clusterColumnCount = 1;
            };

            items.forEach((item) => {
                activeItems = activeItems.filter((activeItem) => activeItem.end > item.start);

                if (activeItems.length === 0) {
                    flushCluster();
                }

                const usedLanes = new Set(activeItems.map((activeItem) => activeItem.lane));
                let lane = 0;

                while (usedLanes.has(lane)) {
                    lane += 1;
                }

                item.lane = lane;
                activeItems.push(item);
                clusterItems.push(item);
                clusterColumnCount = Math.max(clusterColumnCount, activeItems.length, lane + 1);
            });

            flushCluster();
            layoutByEmployee[employeeData.employee.id] = employeeLayout;
        });

        return layoutByEmployee;
    }, [collaboratorData]);

    /**
     * Largura de cada coluna do quadro, na ordem em que os colaboradores são exibidos.
     *
     * A coluna tem uma largura base e ganha um acréscimo limitado por atendimento simultâneo.
     * Sem o teto, um único horário com vários encaixes alargava a coluna inteira e tornava a
     * navegação horizontal do dia impraticável.
     *
     * Base e acréscimo saem de variáveis CSS, então o ajuste de tela pequena continua valendo.
     */
    const collaboratorColumnsTemplate = useMemo(() => {
        if (collaboratorData.length === 0) {
            return undefined;
        }

        return collaboratorData
            .map((employeeData) => {
                const layout = appointmentLayoutByEmployee[employeeData.employee.id] || {};
                const maxSimultaneous = Object.values(layout)
                    .reduce((max, item) => Math.max(max, item.columnCount || 1), 1);
                const extraLanes = Math.max(maxSimultaneous - 1, 0);

                if (extraLanes === 0) {
                    return 'var(--collab-column-base)';
                }

                return `min(var(--collab-column-max), calc(var(--collab-column-base) + ${extraLanes} * var(--collab-column-lane-extra)))`;
            })
            .join(' ');
    }, [collaboratorData, appointmentLayoutByEmployee]);

    const dashboardStats = useMemo(() => {
        const referenceDate = viewMode === 'calendar' ? currentDate : collaboratorDate;
        const monthAppointments = collaboratorScopedAppointments.length;
        const visibleDay = moment(referenceDate).format('YYYY-MM-DD');
        const dayAppointments = collaboratorScopedAppointments.filter((event) => moment(event.start).format('YYYY-MM-DD') === visibleDay);
        const activeCount = dayAppointments.filter((event) => ['CONFIRMED', 'IN_PROGRESS'].includes(event.status)).length;
        const completedCount = dayAppointments.filter((event) => event.status === 'COMPLETED').length;
        const canceledCount = dayAppointments.filter((event) => ['CANCELED', 'NO_SHOW'].includes(event.status)).length;
        const projectedRevenue = dayAppointments
            .filter((event) => event.status !== 'CANCELED' && event.status !== 'NO_SHOW')
            .reduce((total, event) => total + (getAppointmentPriceValue(event.resource) || 0), 0);

        // A divisão clube/avulso ignora cancelado e no-show, mesmo critério da receita
        // projetada acima. Antes contava todo mundo, e o número divergia da agenda: com o
        // botão "Cancelados" desligado (o padrão), a tela mostrava um atendimento e o card
        // dizia dois. Cancelado não some dos indicadores por isso - ele tem os seus
        // próprios (canceledCount aqui e friction no painel lateral). Ficar fora daqui
        // evita, ainda, inflar o lado avulso: cliente sem cadastro cancela mais que
        // assinante, então o cancelamento pesava sempre para o mesmo lado da comparação.
        const isEffectiveAppointment = (event) => event.status !== 'CANCELED' && event.status !== 'NO_SHOW';

        const effectiveDayAppointments = dayAppointments.filter(isEffectiveAppointment);
        const clubAppointmentsCount = effectiveDayAppointments.filter((event) => event.resource?.customer?.clubMember).length;
        const singleAppointmentsCount = effectiveDayAppointments.length - clubAppointmentsCount;

        // Mesma separação clube/avulso, mas sobre o total já carregado no mês (não só o dia em foco).
        const effectiveMonthAppointments = collaboratorScopedAppointments.filter(isEffectiveAppointment);
        const monthClubCount = effectiveMonthAppointments.filter((event) => event.resource?.customer?.clubMember).length;
        const monthSingleCount = effectiveMonthAppointments.length - monthClubCount;
        // Exibido junto de clube/avulso para a conta fechar com o total do mês: sem ele, a
        // soma das duas partes ficaria menor que o total e pareceria erro de cálculo.
        const monthCanceledCount = monthAppointments - effectiveMonthAppointments.length;

        return {
            monthAppointments,
            monthClubCount,
            monthSingleCount,
            monthCanceledCount,
            dayAppointments: dayAppointments.length,
            clubAppointmentsCount,
            singleAppointmentsCount,
            activeCount,
            completedCount,
            canceledCount,
            projectedRevenue,
            referenceDate
        };
    }, [collaboratorScopedAppointments, collaboratorDate, currentDate, viewMode]);

    const statusLegend = useMemo(() => {
        const items = Object.entries(statusConfig).map(([status, config]) => ({
            key: status,
            label: config.label,
            icon: config.icon,
            surface: getStatusSurface(status)
        }));

        // "Concluído sem venda" não é um status do enum, mas é um estado que o quadro pinta
        // com cor própria, então precisa estar na legenda de status e não entre os
        // indicadores. Entra logo depois de Concluído, que é o seu par: a mesma etapa do
        // atendimento, separada pelo que ainda falta fazer.
        const completedIndex = items.findIndex((item) => item.key === 'COMPLETED');
        const pendingSaleItem = {
            key: 'COMPLETED_PENDING_SALE',
            label: 'Concluído sem venda',
            icon: 'fa-solid fa-cash-register',
            surface: PENDING_SALE_SURFACE
        };

        items.splice(completedIndex >= 0 ? completedIndex + 1 : items.length, 0, pendingSaleItem);

        return items;
    }, []);

    const calendarFocusAppointments = useMemo(() => {
        const visibleDay = moment(currentDate).format('YYYY-MM-DD');

        return collaboratorScopedAppointments
            .filter((event) => moment(event.start).format('YYYY-MM-DD') === visibleDay)
            .sort((firstEvent, secondEvent) => firstEvent.start - secondEvent.start);
    }, [collaboratorScopedAppointments, currentDate]);

    const calendarFocusStats = useMemo(() => ({
        total: calendarFocusAppointments.length,
        active: calendarFocusAppointments.filter((event) => ['CONFIRMED', 'IN_PROGRESS'].includes(event.status)).length,
        completed: calendarFocusAppointments.filter((event) => event.status === 'COMPLETED').length,
        friction: calendarFocusAppointments.filter((event) => ['CANCELED', 'NO_SHOW'].includes(event.status)).length
    }), [calendarFocusAppointments]);

    const surfaceTitle = viewMode === 'calendar' ? 'Calendario operacional' : 'Quadro de horários';
    const surfaceDescription = viewMode === 'calendar'
        ? 'Navegue pela semana, enxergue a ocupacao do dia e abra horarios livres sem perder contexto.'
        : 'Comece pelo time do dia e identifique rapidamente quem esta livre, cheio ou com atendimento em andamento.';

    // ─── Clique em slot vazio do quadro colaborador para novo agendamento ─────

    // Distância em pixels a partir da qual o gesto deixa de ser clique e vira arrasto. Abaixo
    // disso é o tremor natural da mão ao clicar, que não pode cancelar a abertura do cartão.
    const BOARD_PAN_THRESHOLD = 5;

    /**
     * Arrastar o cartão remaneja o atendimento: solto em outro horário ou em outra coluna, ele
     * muda de hora e de colaborador; puxando a borda de baixo, muda a duração.
     *
     * Só atendimento em aberto pode ser movido. Concluído, cancelado, não compareceu e em
     * andamento representam algo que já aconteceu (ou está acontecendo) e remanejar a agenda
     * deles não corresponde a nada no mundo real.
     */
    const canDragAppointment = useCallback((appointment) => !isCollaboratorDayClosed
        && ['SCHEDULED', 'CONFIRMED'].includes(appointment?.status), [isCollaboratorDayClosed]);

    const clearCardDrag = useCallback(() => {
        cardDragRef.current = null;
        setCardDragPreview(null);
        document.body.classList.remove('is-dragging-appointment');
    }, []);

    /** Minuto da grade sob o cursor, dentro da coluna informada. */
    const resolveMinutesFromPointer = useCallback((columnElement, clientY) => {
        const rect = columnElement.getBoundingClientRect();
        const offsetY = clientY - rect.top;
        const slotIndex = Math.round(offsetY / COLLABORATOR_BOARD_SLOT_HEIGHT);

        return collaboratorBoardConfig.startMinutes + (slotIndex * COLLABORATOR_BOARD_INTERVAL_MINUTES);
    }, [collaboratorBoardConfig.startMinutes]);

    const handleCardDragMove = useCallback((event) => {
        const drag = cardDragRef.current;
        if (!drag) {
            return;
        }

        if (!drag.moved
                && Math.abs(event.clientX - drag.startX) < BOARD_PAN_THRESHOLD
                && Math.abs(event.clientY - drag.startY) < BOARD_PAN_THRESHOLD) {
            return;
        }

        drag.moved = true;
        boardDidPanRef.current = true; // impede o clique de abrir o detalhe ao soltar
        document.body.classList.add('is-dragging-appointment');

        // A coluna sob o cursor define o colaborador de destino. Fora de qualquer coluna
        // (cursor no cabeçalho, na régua de horas), mantém a última posição válida.
        const elementUnderPointer = document.elementFromPoint(event.clientX, event.clientY);
        const columnElement = elementUnderPointer?.closest('[data-collab-column]');
        const targetColumn = columnElement || drag.lastColumnElement;

        if (!targetColumn) {
            return;
        }

        drag.lastColumnElement = targetColumn;

        if (drag.mode === 'resize') {
            // Redimensionar: a borda de baixo segue o cursor e a duração é a diferença até o
            // início, nunca menor que uma faixa da grade.
            const endMinutes = resolveMinutesFromPointer(targetColumn, event.clientY);
            const duration = Math.max(endMinutes - drag.originalStartMinutes, COLLABORATOR_BOARD_INTERVAL_MINUTES);

            setCardDragPreview({
                mode: 'resize',
                appointmentId: drag.appointment.id,
                employeeId: drag.originalEmployeeId,
                startMinutes: drag.originalStartMinutes,
                duration
            });
            return;
        }

        // Mover: o ponto agarrado dentro do cartão é preservado, senão o cartão saltaria para
        // ficar com o topo embaixo do cursor no primeiro pixel arrastado.
        const pointerMinutes = resolveMinutesFromPointer(targetColumn, event.clientY);
        const startMinutes = Math.max(pointerMinutes - drag.grabOffsetMinutes, 0);

        setCardDragPreview({
            mode: 'move',
            appointmentId: drag.appointment.id,
            employeeId: Number(targetColumn.dataset.collabColumn),
            startMinutes,
            duration: drag.originalDuration
        });
    }, [resolveMinutesFromPointer]);

    const handleCardDragStart = useCallback((event, appointment, employeeId, mode) => {
        if (event.button !== 0 || !canDragAppointment(appointment)) {
            return;
        }

        // O quadro inteiro também arrasta (navegação). Sem isto, mover um cartão arrastaria
        // as duas coisas ao mesmo tempo.
        event.stopPropagation();

        // Começa um gesto novo, então a marca deixada pelo anterior não vale mais.
        //
        // Quem zerava essa marca era o handleBoardPanStart, no quadro, mas a linha acima
        // impede que ele receba o evento quando o mousedown parte de um cartão. Depois de
        // arrastar ou redimensionar, a marca ficava ligada e engolia o próximo clique
        // naquele cartão: o operador precisava clicar em outra coluna (onde o pan roda e
        // limpa a marca) para só então conseguir abrir o detalhe.
        boardDidPanRef.current = false;

        const startMoment = moment(appointment.start);
        const startMinutes = startMoment.hours() * 60 + startMoment.minutes();
        const columnElement = event.currentTarget.closest('[data-collab-column]');

        cardDragRef.current = {
            mode,
            appointment,
            moved: false,
            startX: event.clientX,
            startY: event.clientY,
            originalEmployeeId: employeeId,
            originalStartMinutes: startMinutes,
            originalDuration: resolveDuration(getDurationInMinutes(appointment.resource)),
            // Distância entre o topo do cartão e o ponto onde o operador agarrou.
            grabOffsetMinutes: columnElement
                ? resolveMinutesFromPointer(columnElement, event.clientY) - startMinutes
                : 0,
            lastColumnElement: columnElement
        };
    }, [resolveMinutesFromPointer, canDragAppointment]);


    /**
     * Grava o remanejamento feito no arrasto.
     *
     * Reaproveita o mesmo PATCH do painel de reagendamento, com o agendamento completo: é o
     * caminho que o servidor já valida (expediente, bloqueio, conflito e almoço), então um
     * destino inválido volta como mensagem em vez de virar agendamento quebrado.
     */
    const applyCardDrag = useCallback(async (preview, appointment, customerAgreedWithNewSchedule = false) => {
        const detailed = await fetchAppointmentWithServices(appointment.resource || appointment);
        const scheduledDate = moment(collaboratorDate).startOf('day').add(preview.startMinutes, 'minutes');

        const serviceItems = Array.isArray(detailed.serviceItems)
            ? detailed.serviceItems.filter((item) => item?.service?.id).map((item) => ({
                service: { id: item.service.id },
                price: item.price != null ? Number(item.price) : null
            }))
            : [];

        const payload = {
            uuid: detailed.uuid,
            customer: detailed.customer ? { id: detailed.customer.id } : null,
            employee: { id: preview.employeeId },
            service: detailed.service ? { id: detailed.service.id } : null,
            serviceItems,
            appointmentStatus: detailed.appointmentStatus,
            scheduledDate: toLocalISOString(scheduledDate.toDate()),
            duration: preview.duration,
            price: detailed.price != null ? detailed.price : null,
            notes: detailed.notes || null,
            branch: detailed.branch ? { id: detailed.branch.id } : null,
            cancellationReason: detailed.cancellationReason || null,
            // Mudar o horário derruba a confirmação do cliente, a menos que a recepção afirme
            // que já combinou o novo horário com ele (ver AppointmentController).
            customerAgreedWithNewSchedule
        };

        axiosInstance.patch(`${Api.appointment.appointment}/${detailed.id}`, payload)
            .then(() => {
                // Aviso discreto: o remanejamento já foi confirmado no diálogo e o resultado
                // está na tela, com o cartão no horário novo. Um alerta com "OK" aqui cobra
                // um clique para reconhecer o que o operador acabou de ver acontecer.
                successToast('Agendamento atualizado.');
                refreshAppointments();
            })
            .catch((error) => errorObjectAlert(error));
    }, [collaboratorDate, refreshAppointments]);

    const finishCardDrag = useCallback(() => {
        const drag = cardDragRef.current;
        const preview = cardDragPreview;

        clearCardDrag();

        if (!drag || !drag.moved || !preview) {
            return;
        }

        // Tira o foco do cartão antes de abrir o diálogo.
        //
        // O cartão é focável (tabIndex) para poder ser aberto pelo teclado, e o mousedown do
        // arrasto o deixa focado. O SweetAlert devolve o foco ao elemento anterior quando
        // fecha, então o Enter que confirma o diálogo (ou a mensagem de sucesso) chegava no
        // cartão logo em seguida e abria o detalhe sem ninguém ter pedido.
        if (document.activeElement instanceof HTMLElement
                && document.activeElement.closest('.collab-apt-card')) {
            document.activeElement.blur();
        }

        const movedInTime = preview.startMinutes !== drag.originalStartMinutes;
        const movedToAnotherEmployee = String(preview.employeeId) !== String(drag.originalEmployeeId);
        const durationChanged = preview.duration !== drag.originalDuration;

        // Soltar no mesmo lugar não é remanejamento: não vale pedir confirmação nem gravar.
        if (!movedInTime && !movedToAnotherEmployee && !durationChanged) {
            return;
        }

        const customerName = getDisplayName(drag.appointment.resource?.customer, 'Cliente');
        const targetTime = moment(collaboratorDate)
            .startOf('day')
            .add(preview.startMinutes, 'minutes')
            .format('HH:mm');

        const question = preview.mode === 'resize'
            ? `Alterar a duração do atendimento de ${customerName} para ${preview.duration} minutos?`
            : `Mover o atendimento de ${customerName} para ${targetTime}${movedToAnotherEmployee ? ', com outro colaborador' : ''}?`;

        // Confirma antes de gravar: um arrasto acidental no meio do expediente remarcaria o
        // cliente sem ninguém perceber, e o sistema não tem desfazer.
        const wasConfirmedForAnotherTime = drag.appointment.status === 'CONFIRMED' && movedInTime;

        if (wasConfirmedForAnotherTime) {
            // O cliente confirmou o horário anterior. Quem sabe se ele já concordou com o novo
            // é a recepção, que acabou de falar com ele — perguntar aqui evita tanto derrubar
            // uma confirmação real quanto manter uma confirmação que não existe.
            warnAlertTwoChoices(`${question} O cliente já concordou com o novo horário?`, {
                confirmText: 'Sim, manter confirmado',
                denyText: 'Não, voltar para agendado',
                onConfirm: () => applyCardDrag(preview, drag.appointment, true),
                onDeny: () => applyCardDrag(preview, drag.appointment, false)
            });
            return;
        }

        warnAlertYesOrNo(question, () => applyCardDrag(preview, drag.appointment));
    }, [cardDragPreview, clearCardDrag, applyCardDrag, collaboratorDate]);

    const stopBoardPan = useCallback(() => {
        if (!boardPanRef.current) {
            return;
        }

        boardPanRef.current = null;
        collaboratorBoardShellRef.current?.classList.remove('is-panning');
    }, []);

    const handleBoardPanMove = useCallback((event) => {
        const pan = boardPanRef.current;
        if (!pan) {
            return;
        }

        const deltaX = event.clientX - pan.startX;
        const deltaY = event.clientY - pan.startY;

        if (!boardDidPanRef.current
                && Math.abs(deltaX) < BOARD_PAN_THRESHOLD && Math.abs(deltaY) < BOARD_PAN_THRESHOLD) {
            return;
        }

        boardDidPanRef.current = true;
        collaboratorBoardShellRef.current?.classList.add('is-panning');

        // As duas rolagens moram no shell, então o gesto move um elemento só.
        if (collaboratorBoardShellRef.current) {
            collaboratorBoardShellRef.current.scrollLeft = pan.startScrollLeft - deltaX;
            collaboratorBoardShellRef.current.scrollTop = pan.startScrollTop - deltaY;
        }
    }, []);

    /**
     * Inicia o arrasto para navegar pelo quadro.
     *
     * Só com o botão principal, e nunca a partir de um controle: o botão de cancelar bloqueio
     * dentro da coluna precisa continuar clicável.
     */
    const handleBoardPanStart = useCallback((event) => {
        if (event.button !== 0 || event.target.closest('button, a, input, select, textarea')) {
            return;
        }

        boardDidPanRef.current = false;
        boardPanRef.current = {
            startX: event.clientX,
            startY: event.clientY,
            startScrollLeft: collaboratorBoardShellRef.current?.scrollLeft || 0,
            startScrollTop: collaboratorBoardShellRef.current?.scrollTop || 0
        };
    }, []);

    // Escuta no documento, e não no elemento: o cursor costuma sair do quadro no meio do
    // gesto, e sem isso a navegação travava no meio do arrasto.
    useEffect(() => {
        const handleMove = (event) => {
            handleCardDragMove(event);
            handleBoardPanMove(event);
        };

        const handleUp = () => {
            finishCardDrag();
            stopBoardPan();
        };

        document.addEventListener('mousemove', handleMove);
        document.addEventListener('mouseup', handleUp);

        return () => {
            document.removeEventListener('mousemove', handleMove);
            document.removeEventListener('mouseup', handleUp);
        };
    }, [handleBoardPanMove, stopBoardPan, handleCardDragMove, finishCardDrag]);

    const handleCollaboratorBoardSlotClick = useCallback((e, empData) => {
        // Se o mousedown que originou este click partiu de um card, ignora.
        // Cobre click com movimento: mousedown no card, mouseup fora do card.
        if (colBodyMouseDownTargetRef.current?.closest('.collab-apt-card')) return;

        // O gesto foi um arrasto para navegar pelo quadro, não um clique em horário livre.
        if (boardDidPanRef.current) return;

        if (isCollaboratorDayClosed) {
            warnAlert('A filial está fechada neste dia. Escolha outra data para agendar.');
            return;
        }

        if (empData.isOnVacation) {
            warnAlert(`${vacationMessage(empData.employee, getDisplayName(empData.employee, 'O colaborador'))} Escolha outra data ou outro profissional.`);
            return;
        }

        if (empData.isDayOff) {
            warnAlert(`${getDisplayName(empData.employee, 'O colaborador')} não atende neste dia da semana. Escolha outra data ou outro profissional.`);
            return;
        }

        const colBodyRect = e.currentTarget.getBoundingClientRect();
        const rawY = e.clientY - colBodyRect.top;
        const slotIndex = Math.floor(Math.max(0, rawY) / COLLABORATOR_BOARD_SLOT_HEIGHT);
        const clickedMinutes = collaboratorBoardConfig.startMinutes + (slotIndex * COLLABORATOR_BOARD_INTERVAL_MINUTES);
        const clickedDate = moment(collaboratorDate).startOf('day').add(clickedMinutes, 'minutes').toDate();

        // Pré-seleciona colaborador e filial para que o planner já carregue os
        // horários disponíveis assim que o serviço for selecionado.
        setPlannerEmployee({
            value: empData.employee.id,
            label: getDisplayName(empData.employee, 'Colaborador'),
            employee: empData.employee
        });

        // A filial define o expediente que o planner usa para listar horários, então ela
        // precisa ser a filial DESTE colaborador. Com "todas as filiais" no topo,
        // selectedBranch é nulo: antes o planner ficava com a filial da última vez que foi
        // aberto (o estado não é limpo ao fechar) e listava os horários da unidade errada.
        // O colaborador do quadro carrega a filial dele, que é a resposta certa nesse caso.
        const employeeBranches = Array.isArray(empData.employee?.branches) ? empData.employee.branches : [];
        // Só resolve pelo colaborador quando ele atende em uma única filial; com mais de
        // uma não há como adivinhar, e aí o operador escolhe no próprio planner.
        const branchToUse = selectedBranch ?? (employeeBranches.length === 1 ? employeeBranches[0] : null);

        if (branchToUse) {
            setPlannerBranch((current) => {
                if (current?.value === branchToUse.id) return current;
                const matched = plannerBranchOptions.find((option) => option.value === branchToUse.id);
                return matched ?? { value: branchToUse.id, label: branchToUse.name, branch: branchToUse };
            });
        } else {
            // Sem filial resolvida, começar vazio é melhor que herdar a filial anterior:
            // o planner pede a seleção em vez de mostrar horário de outra unidade.
            setPlannerBranch(null);
        }

        setSlotPlannerDate(clickedDate);
        setSlotPlannerError('');
        setPlannerSlots([]);
        setSlotPlannerOpen(true);
    }, [collaboratorBoardConfig.startMinutes, collaboratorDate, selectedBranch, plannerBranchOptions, isCollaboratorDayClosed]);

    const getAppointmentBlockStyle = useCallback((appointment, employeeId) => {
        const startMoment = moment(appointment.start);
        const endMoment = moment(appointment.end);
        // Durante o arrasto o cartão é desenhado na posição de destino, não na gravada: sem
        // essa prévia o operador arrastaria às cegas e só veria o resultado depois de salvar.
        const isBeingDragged = cardDragPreview?.appointmentId === appointment.id
            && String(cardDragPreview.employeeId) === String(employeeId);
        const startMinutes = isBeingDragged
            ? cardDragPreview.startMinutes
            : startMoment.hours() * 60 + startMoment.minutes();
        const endMinutes = isBeingDragged
            ? cardDragPreview.startMinutes + cardDragPreview.duration
            : endMoment.hours() * 60 + endMoment.minutes();
        const statusSurface = getStatusSurface(appointment.status);
        const top = ((startMinutes - collaboratorBoardConfig.startMinutes) / COLLABORATOR_BOARD_INTERVAL_MINUTES) * COLLABORATOR_BOARD_SLOT_HEIGHT + 2;
        const baseHeight = ((endMinutes - startMinutes) / COLLABORATOR_BOARD_INTERVAL_MINUTES) * COLLABORATOR_BOARD_SLOT_HEIGHT;
        // O piso de 28px também cobre o atendimento de duração zero (serviço que não
        // reserva janela): ele aparece como um cartão fino em vez de sumir do quadro.
        const height = Math.max(baseHeight - 6, 28);
        const layout = appointmentLayoutByEmployee[employeeId]?.[getAppointmentLayoutKey(appointment)] || { lane: 0, columnCount: 1 };
        const columnWidth = 100 / layout.columnCount;
        const horizontalGap = layout.columnCount > 1 ? 3 : 4;

        const clubMember = appointment.resource?.customer?.clubMember;
        let background = statusSurface.background;
        let borderColor = statusSurface.border;
        let color = statusSurface.text;

        // O dourado identifica o clube enquanto o atendimento está aberto. Depois de
        // concluído, o status operacional precisa prevalecer para a mudança ficar visível.
        if (clubMember && ['SCHEDULED', 'CONFIRMED', 'IN_PROGRESS'].includes(appointment.status)) {
            background = 'linear-gradient(135deg, #f7d070 0%, #fdf6cf 40%, #dfb244 70%, #c39627 100%)';
            borderColor = '#c39627';
            color = '#3b2501';
        }

        // Concluído se divide em dois estados operacionais bem diferentes: o cinza é o
        // atendimento encerrado de vez (venda registrada, nada a fazer) e o petróleo é o que
        // ainda espera a venda ser gerada. Com a mesma cor nos dois, a recepção precisava
        // abrir cartão por cartão para saber quais ainda davam trabalho.
        if (appointment.status === 'COMPLETED' && !appointment.resource?.saleGenerated) {
            background = PENDING_SALE_SURFACE.background;
            borderColor = PENDING_SALE_SURFACE.border;
            color = PENDING_SALE_SURFACE.text;
        }

        return {
            top: `${top}px`,
            height: `${height}px`,
            left: `calc(${layout.lane * columnWidth}% + ${horizontalGap}px)`,
            width: `calc(${columnWidth}% - ${horizontalGap * 2}px)`,
            borderColor: borderColor,
            background: background,
            color: color
        };
    }, [appointmentLayoutByEmployee, collaboratorBoardConfig.startMinutes, cardDragPreview]);

    const getEmployeeBlockedPeriod = useCallback((employee) => {
        const parseTime = (value) => {
            const match = String(value || '').match(/^(\d{1,2}):(\d{2})/);
            if (!match) return null;

            return (Number(match[1]) * 60) + Number(match[2]);
        };
        const startMinutes = parseTime(employee?.lunchStartTime);
        const endMinutes = parseTime(employee?.lunchEndTime);

        if (startMinutes == null || endMinutes == null || endMinutes <= startMinutes) {
            return null;
        }

        const visibleStart = Math.max(startMinutes, collaboratorBoardConfig.startMinutes);
        const visibleEnd = Math.min(endMinutes, collaboratorBoardConfig.endMinutes);
        if (visibleEnd <= visibleStart) return null;

        return {
            label: `${String(employee.lunchStartTime).slice(0, 5)} - ${String(employee.lunchEndTime).slice(0, 5)}`,
            style: {
                top: `${((visibleStart - collaboratorBoardConfig.startMinutes) / COLLABORATOR_BOARD_INTERVAL_MINUTES) * COLLABORATOR_BOARD_SLOT_HEIGHT}px`,
                height: `${Math.max(((visibleEnd - visibleStart) / COLLABORATOR_BOARD_INTERVAL_MINUTES) * COLLABORATOR_BOARD_SLOT_HEIGHT, 28)}px`
            }
        };
    }, [collaboratorBoardConfig.endMinutes, collaboratorBoardConfig.startMinutes]);

    // A folga cobre a grade inteira do dia, não uma faixa de horário: por isso o estilo é um
    // só para todas as colunas, em vez de ser calculado por colaborador como os demais
    // bloqueios.
    const dayOffBlockStyle = useMemo(() => ({
        top: 0,
        height: `${collaboratorBoardConfig.totalHeight}px`
    }), [collaboratorBoardConfig.totalHeight]);

    const getRegisteredBlockStyle = useCallback((block) => {
        const start = moment(block.startDate);
        const end = moment(block.endDate);
        const startMinutes = (start.hours() * 60) + start.minutes();
        const endMinutes = (end.hours() * 60) + end.minutes();
        return {
            top: `${((startMinutes - collaboratorBoardConfig.startMinutes) / COLLABORATOR_BOARD_INTERVAL_MINUTES) * COLLABORATOR_BOARD_SLOT_HEIGHT}px`,
            height: `${Math.max(((endMinutes - startMinutes) / COLLABORATOR_BOARD_INTERVAL_MINUTES) * COLLABORATOR_BOARD_SLOT_HEIGHT, 28)}px`
        };
    }, [collaboratorBoardConfig.startMinutes]);

    const renderCollaboratorAppointment = useCallback((appointment, employeeId) => {
        const appointmentLayout = appointmentLayoutByEmployee[employeeId]?.[getAppointmentLayoutKey(appointment)] || { lane: 0, columnCount: 1 };
        const durationMinutes = Math.max(moment(appointment.end).diff(moment(appointment.start), 'minutes'), 0);
        const isCompactAppointment = appointmentLayout.columnCount > 1 || durationMinutes <= 20;
        return (
            <div
                key={appointment.id}
                role="button"
                tabIndex={0}
                className={`collab-apt-card ${appointment.resource?.encaixe ? 'is-encaixe' : ''} ${appointment.status === 'COMPLETED' ? 'is-completed' : ''} ${appointment.status === 'COMPLETED' && !appointment.resource?.saleGenerated ? 'is-pending-sale' : ''} ${selectedAppointment?.id === appointment.resource?.id ? 'is-selected' : ''} ${appointmentLayout.columnCount > 1 ? 'is-overlap' : ''} ${isCompactAppointment ? 'is-compact' : ''} ${canDragAppointment(appointment) ? 'is-draggable' : ''} ${cardDragPreview?.appointmentId === appointment.id ? 'is-dragging' : ''}`}
                style={getAppointmentBlockStyle(appointment, employeeId)}
                onMouseDown={(e) => handleCardDragStart(e, appointment, employeeId, 'move')}
                onClick={(e) => {
                    e.stopPropagation();
                    // Arrastar a partir de um cartão navega pelo quadro; não abre o detalhe.
                    if (boardDidPanRef.current) return;
                    handleSelectEvent(appointment);
                }}
                onKeyDown={(e) => {
                    if (e.key !== 'Enter' && e.key !== ' ') {
                        return;
                    }
                    e.preventDefault();
                    // Mesma guarda do clique: um gesto de arrasto não abre o detalhe.
                    if (boardDidPanRef.current) {
                        return;
                    }
                    handleSelectEvent(appointment);
                }}
                title={`${moment(appointment.start).format('HH:mm')} - ${moment(appointment.end).format('HH:mm')} | ${getDisplayName(appointment.resource?.customer, 'Cliente')} | ${getServicesLabel(appointment.resource, 'Serviço')}`}
            >
                <div className="collab-apt-top">
                    <div className="collab-apt-time">
                        <i className="fa-regular fa-clock"></i>
                        <span>{moment(appointment.start).format('HH:mm')} - {moment(appointment.end).format('HH:mm')}</span>
                    </div>
                    <div className="collab-apt-badge-group" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <span className="collab-apt-price-badge">{formatAppointmentPriceLabel(appointment.resource)}</span>
                        {appointment.resource?.encaixe && (
                            // Sinaliza que este agendamento é um encaixe (sobreposto a outro horário).
                            <i className="fa-solid fa-arrows-to-dot" style={{ color: '#fd7e14' }} title="Encaixe"></i>
                        )}
                        {appointment.resource?.appointmentOrigin === 'PUBLIC' && (
                            <i className="fa-solid fa-mobile-screen-button" style={{ color: '#2563eb' }} title="Agendamento pelo app do cliente"></i>
                        )}
                        {appointment.resource?.appointmentOrigin === 'AI' && (
                            <i className="fa-solid fa-robot" style={{ color: '#16a34a' }} title="Agendamento feito pela IA"></i>
                        )}
                        {appointment.resource?.appointmentOrigin === 'RECEPTION' && (
                            <i className="fa-solid fa-desktop" style={{ color: '#475467' }} title="Agendamento feito pela recepção"></i>
                        )}
                        {appointment.resource?.appointmentOrigin === 'COLLABORATOR_PORTAL' && (
                            <i className="fa-solid fa-user-tie" style={{ color: '#7c3aed' }} title="Agendamento feito pelo colaborador no portal"></i>
                        )}
                        {appointment.resource?.depositSituation && (
                            // Reserva paga para garantir o horário: a cor mostra se está pendente,
                            // paga, retida (cliente perdeu o horário), abatida ou devolvida.
                            <AppointmentDepositBadge
                                situation={appointment.resource.depositSituation}
                                value={appointment.resource.depositValue} />
                        )}
                        {appointment.resource?.saleGenerated && (
                            // Indica que a venda já foi registrada para este agendamento.
                            <i className="fa-solid fa-receipt" style={{ color: '#374151' }} title="Venda registrada"></i>
                        )}
                        {appointment.resource?.confirmedViaWhatsappAt && (
                            <i className="fa-brands fa-whatsapp collab-apt-whatsapp-badge"
                                title="Confirmado pelo cliente via WhatsApp"></i>
                        )}
                        {!appointment.resource?.confirmedViaWhatsappAt
                            && appointment.resource?.whatsappConfirmationSentAt && (
                            // O envio da mensagem não significa que o cliente confirmou;
                            // o ícone separado evita apresentar uma pendência como confirmação.
                            <i className="fa-regular fa-clock collab-apt-whatsapp-pending-badge"
                                title="Aguardando confirmação do cliente via WhatsApp"></i>
                        )}
                        {appointment.resource?.customer?.clubLate && (
                            // Fecha a fileira do topo, no canto do cartão: é o ponto mais visível na
                            // varredura do quadro e não empurra o horário, por não ter rótulo.
                            <i className="fa-solid fa-circle-exclamation collab-apt-late-icon"
                                title="Cliente com mensalidade do Clube em atraso"></i>
                        )}
                        {appointment.resource?.servicesDurationExceeded && (
                            <i className="fa-solid fa-triangle-exclamation collab-apt-duration-exception"
                                title="Exceção de duração: serviços adicionais ultrapassam a duração reservada deste agendamento"
                                aria-label="Exceção de duração"></i>
                        )}
                    </div>
                </div>
                <div className="collab-apt-customer">
                    <i className="fa-regular fa-user"></i>
                    <span>
                        {getDisplayName(appointment.resource?.customer, 'Cliente')}
                        {appointment.resource?.firstVisit && (
                            <i className="fa-solid fa-star appointment-first-visit-icon ms-1"
                                title="Primeiro atendimento deste cliente" aria-label="Primeiro atendimento" />
                        )}
                        {appointment.resource?.customerBirthday && (
                            <i className="fa-solid fa-cake-candles appointment-customer-birthday-icon ms-1"
                                title="Aniversário do cliente" aria-label="Aniversário do cliente" />
                        )}
                        {appointment.resource?.customer?.clubMember && (
                            <ClubCrownIcon className="club-crown-badge ms-1" />
                        )}
                    </span>
                </div>
                {appointment.resource?.customer?.clubPlanName && (
                    <div
                        className="collab-apt-club-plan"
                        title={`Plano do Clube: ${appointment.resource.customer.clubPlanName}`}
                    >
                        <i className="fa-solid fa-gem"></i>
                        <span>{appointment.resource.customer.clubPlanName}</span>
                    </div>
                )}
                {
                    // Alça de duração: arrastar a borda de baixo estica ou encurta o
                    // atendimento. Fica só no que pode ser remanejado.
                    canDragAppointment(appointment) ?
                        <span
                            className="collab-apt-card__resize-handle"
                            title="Arraste para ajustar a duração"
                            onMouseDown={(e) => handleCardDragStart(e, appointment, employeeId, 'resize')}
                        />
                        :
                        <></>
                }
                {!isCompactAppointment && appointment.resource?.customer?.branch?.name && (
                    <div className="collab-apt-branch-origin" title={`Unidade de origem do cliente: ${appointment.resource.customer.branch.name}`}>
                        <i className="fa-solid fa-location-dot"></i>
                        <span>{appointment.resource.customer.branch.name}</span>
                    </div>
                )}
                {!isCompactAppointment && (
                    <div className="collab-apt-service">
                        <i className="fa fa-list-ul"></i>
                        <span>{getServicesLabel(appointment.resource, 'Serviço')}</span>
                    </div>
                )}
            </div>
        );
    }, [appointmentLayoutByEmployee, getAppointmentBlockStyle, handleSelectEvent, selectedAppointment, handleCardDragStart, cardDragPreview?.appointmentId, canDragAppointment]);

    return (
        <>
            <section className={`appointment-dashboard-shell ${standalone ? 'is-standalone' : ''} ${viewMode === 'collaborator' ? 'is-collaborator-priority' : ''}`}>
                {/* Mesmo cabeçalho das demais telas. O hero anterior gastava altura repetindo
                    o que já está logo abaixo: filial e data ficam na barra do quadro e o total
                    do dia aparece no resumo "Visão do período". */}
                {!standalone && <PageTitle motherMenu={t('appointments')} activeMenu="Agenda por colaborador" />}

                {!standalone && <Row className={`g-4 mb-4 appointment-dashboard-top-row ${viewMode === 'collaborator' ? 'is-collaborator-priority' : ''}`}>
                    <Col xl={12}>
                        <div className={`appointment-dashboard-kpis ${viewMode === 'collaborator' ? 'is-compact is-collaborator-summary' : ''}`}>
                            <div className="appointment-dashboard-kpis__header">
                                <DashboardSectionHeader
                                    icon="fa-calendar-day"
                                    accentClass="dashboard-accent-card dashboard-accent-card--sky"
                                    eyebrow="Visão do período"
                                    title={moment(dashboardStats.referenceDate).locale(calendarLocale).format('dddd, DD [de] MMMM')}
                                    subtitle="Resumo operacional dos atendimentos na data selecionada."
                                    action={(
                                        <span className="appointment-dashboard-kpis__period-tag">
                                            {viewMode === 'calendar' ? 'Leitura mensal' : 'Leitura diária'}
                                        </span>
                                    )}
                                />
                            </div>

                            <div className="appointment-dashboard-kpis__grid">
                                <div className="appointment-dashboard-kpi-card is-highlight">
                                    <span>No dia</span>
                                    <strong>{dashboardStats.dayAppointments}</strong>
                                    <small>{viewMode === 'calendar' ? 'atendimentos no foco atual' : 'atendimentos distribuídos por colaborador'}</small>
                                </div>
                                <div className="appointment-dashboard-kpi-card">
                                    <span>Avulsos</span>
                                    <strong>{dashboardStats.singleAppointmentsCount}</strong>
                                    <small>sem cancelados e no-show</small>
                                </div>
                                <div className="appointment-dashboard-kpi-card">
                                    <span>Assinatura (Clube)</span>
                                    <strong>{dashboardStats.clubAppointmentsCount}</strong>
                                    <small>sem cancelados e no-show</small>
                                </div>
                                <div className="appointment-dashboard-kpi-card">
                                    <span>Ativos</span>
                                    <strong>{dashboardStats.activeCount}</strong>
                                    <small>confirmados ou em andamento</small>
                                </div>
                                <div className="appointment-dashboard-kpi-card">
                                    <span>Concluídos</span>
                                    <strong>{dashboardStats.completedCount}</strong>
                                    <small>finalizados no dia</small>
                                </div>
                                <div className="appointment-dashboard-kpi-card">
                                    <span>Cancelados</span>
                                    <strong>{dashboardStats.canceledCount}</strong>
                                    <small>cancelados no dia</small>
                                </div>
                                <div className="appointment-dashboard-kpi-card">
                                    <span>Receita prevista</span>
                                    <strong>{formatAppointmentPrice(dashboardStats.projectedRevenue)}</strong>
                                    <small>sem cancelados e no-show</small>
                                </div>
                            </div>

                            <div className="appointment-dashboard-kpis__footer">
                                <span>
                                    <strong>{dashboardStats.monthAppointments}</strong> agendamentos no mes
                                    {' '}(<strong>{dashboardStats.monthClubCount}</strong> com clube · <strong>{dashboardStats.monthSingleCount}</strong> avulsos
                                    {dashboardStats.monthCanceledCount > 0 && (
                                        <> · <strong>{dashboardStats.monthCanceledCount}</strong> cancelados</>
                                    )})
                                </span>
                                <span>{dashboardStats.canceledCount} com friccao no dia</span>
                            </div>
                        </div>
                    </Col>
                </Row>}

            <Row>
                <Col xl={12}>
                    <Card className={`appointment-dashboard-surface ${viewMode === 'collaborator' ? 'is-collaborator-view' : ''}`}>
                        <Card.Header className="appointment-dashboard-surface__header">
                            {viewMode === 'calendar' ? (
                                <div className="appointment-dashboard-surface__heading">
                                    <div className="appointment-dashboard-surface__title-block">
                                        <div className="appointment-dashboard-surface__title-wrap">
                                            <div className="appointment-dashboard-surface__icon">
                                                <i className="fas fa-calendar-alt"></i>
                                            </div>
                                            <div>
                                                <Card.Title className="mb-0">{surfaceTitle}</Card.Title>
                                                <small>{surfaceDescription}</small>
                                            </div>
                                        </div>
                                        <StatusLegend items={statusLegend} />
                                        <div className="appointment-dashboard-collaborator-controls">
                                            <div className="appointment-dashboard-collaborator-filter">
                                                <label className="appointment-dashboard-collaborator-filter__label">Colaborador</label>
                                                <CustomSelect
                                                    value={selectedCollaboratorFilter}
                                                    onChange={(option) => setSelectedCollaboratorFilter(option)}
                                                    options={collaboratorFilterOptions}
                                                    isClearable
                                                    placeholder="Todos"
                                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 20000 }) }}
                                                />
                                            </div>
                                            <div className="appointment-dashboard-canceled-filter">
                                                <label className="appointment-dashboard-collaborator-filter__label">Cancelados</label>
                                                <CustomSelect
                                                    value={canceledVisibilityOptions.find((option) => option.value === showCanceledAppointments)}
                                                    onChange={(option) => setShowCanceledAppointments(Boolean(option?.value))}
                                                    options={canceledVisibilityOptions}
                                                    isClearable={false}
                                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 20000 }) }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="appointment-dashboard-surface__heading is-split">
                                    <div className="appointment-dashboard-surface__title-block">
                                        {/* Sem subtítulo: a frase explicativa consumia uma linha
                                            inteira do cabeçalho e empurrava a navegação de data
                                            contra os filtros. O quadro logo abaixo já se explica. */}
                                        <div className="appointment-dashboard-surface__title-wrap is-compact">
                                            <div className="appointment-dashboard-surface__icon">
                                                <i className="fas fa-users"></i>
                                            </div>
                                            <Card.Title className="mb-0">{surfaceTitle}</Card.Title>
                                            {standalone && <small>{selectedBranch?.name || 'Todas as filiais'}</small>}
                                        </div>
                                        <div className="appointment-dashboard-collaborator-controls">
                                            <div className="appointment-dashboard-collaborator-filter">
                                                <label className="appointment-dashboard-collaborator-filter__label">Colaborador</label>
                                                <CustomSelect
                                                    value={selectedCollaboratorFilter}
                                                    onChange={(option) => setSelectedCollaboratorFilter(option)}
                                                    options={collaboratorFilterOptions}
                                                    isClearable
                                                    placeholder="Todos"
                                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 20000 }) }}
                                                />
                                            </div>
                                            <div className="appointment-dashboard-canceled-filter">
                                                <label className="appointment-dashboard-collaborator-filter__label">Cancelados</label>
                                                <CustomSelect
                                                    value={canceledVisibilityOptions.find((option) => option.value === showCanceledAppointments)}
                                                    onChange={(option) => setShowCanceledAppointments(Boolean(option?.value))}
                                                    options={canceledVisibilityOptions}
                                                    isClearable={false}
                                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 20000 }) }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="appointment-dashboard-date-nav">
                                        <a
                                            className="collab-nav-btn appointment-open-standalone-btn"
                                            href={standalone ? '/appointment/dashboard' : `/appointment/agenda?${new URLSearchParams({
                                                date: moment(collaboratorDate).format('YYYY-MM-DD'),
                                                canceled: String(showCanceledAppointments),
                                                ...(selectedBranch?.id ? { branch: String(selectedBranch.id) } : {}),
                                                ...(selectedCollaboratorFilter?.value ? { collaborator: String(selectedCollaboratorFilter.value) } : {})
                                            })}`}
                                            target={standalone ? undefined : '_blank'}
                                            rel={standalone ? undefined : 'noopener noreferrer'}
                                            title={standalone ? 'Voltar ao sistema' : 'Abrir agenda em nova aba'}
                                            aria-label={standalone ? 'Voltar ao sistema' : 'Abrir agenda em nova aba'}
                                        >
                                            <i className={`fas ${standalone ? 'fa-arrow-left' : 'fa-up-right-from-square'}`} aria-hidden="true"></i>
                                        </a>
                                        <button
                                            className="collab-nav-btn"
                                            onClick={() => handleCollaboratorDateChange(moment(collaboratorDate).subtract(1, 'day').toDate())}
                                        >
                                            <i className="fas fa-chevron-left"></i>
                                        </button>
                                        {/* Só o dia, numa linha: o ano empilhado deixava o rótulo
                                            mais alto que os botões ao lado e não informa nada em
                                            uma agenda que se navega dia a dia. */}
                                        <div className="appointment-dashboard-date-nav__label">
                                            <span className="appointment-dashboard-date-nav__title">
                                                {moment(collaboratorDate).locale(calendarLocale).format('dddd, DD [de] MMMM')}
                                            </span>
                                        </div>
                                        <button
                                            className="collab-nav-btn"
                                            onClick={() => handleCollaboratorDateChange(moment(collaboratorDate).add(1, 'day').toDate())}
                                        >
                                            <i className="fas fa-chevron-right"></i>
                                        </button>
                                        <button
                                            className="collab-nav-btn collab-today-btn"
                                            onClick={() => handleCollaboratorDateChange(new Date())}
                                        >
                                            Hoje
                                        </button>
                                        {/* Saltar direto para uma data. As setas resolvem o dia
                                            seguinte; para a semana que vem, avançar de um em um
                                            é trabalhoso. */}
                                        <DatePicker
                                            selected={collaboratorDate}
                                            onChange={(date) => date && handleCollaboratorDateChange(date)}
                                            locale="pt-BR"
                                            dateFormat="dd/MM/yyyy"
                                            customInput={<BoardDatePickerButton />}
                                            popperClassName="colmeia-datepicker-popper collab-datepicker-popper"
                                            popperPlacement="bottom-end"
                                            withPortal={isNarrowScreen}
                                            portalId="collab-datepicker-portal"
                                        />
                                        <button
                                            className="collab-nav-btn collab-new-apt-btn"
                                            onClick={() => openSlotPlanner(collaboratorDate)}
                                            title="Novo agendamento"
                                        >
                                            <i className="fas fa-plus"></i>
                                            <span>Novo</span>
                                        </button>
                                        <button
                                            type="button"
                                            className="collab-nav-btn collab-new-apt-btn collab-encaixe-btn"
                                            onClick={openEncaixePlanner}
                                            title="Criar encaixe"
                                        >
                                            <i className="fa-solid fa-arrows-to-dot" aria-hidden="true"></i>
                                            <span>Encaixe</span>
                                        </button>
                                    </div>

                                    <StatusLegend items={statusLegend} />
                                </div>
                            )}
                        </Card.Header>
                        <Card.Body className={`appointment-dashboard-surface__body ${viewMode === 'collaborator' ? 'is-collaborator-view' : ''}`}>
                            {viewMode === 'calendar' ? (
                                <div className="appointment-dashboard-calendar-wrap">
                                    <div className="appointment-dashboard-calendar-hint">
                                        <div>
                                            <strong>Modo rapido:</strong> clique em um espaco vazio do calendario para abrir horarios livres e criar um agendamento em contexto.
                                        </div>
                                        <span>
                                            {selectedAppointment ? 'Um agendamento esta selecionado abaixo.' : 'Selecione um atendimento na grade ou no resumo lateral para abrir detalhes e acoes.'}
                                        </span>
                                    </div>

                                    <div className="appointment-dashboard-calendar-grid">
                                        <div className="appointment-dashboard-calendar-main">
                                            <Calendar
                                                localizer={localizer}
                                                culture={calendarLocale}
                                                events={visibleAppointments}
                                                date={currentDate}
                                                view={calendarView}
                                                views={['week', 'day', 'month']}
                                                startAccessor="start"
                                                endAccessor="end"
                                                selectable
                                                popup
                                                step={30}
                                                timeslots={2}
                                                min={calendarTimeRange.min}
                                                max={calendarTimeRange.max}
                                                onView={setCalendarView}
                                                onNavigate={handleCalendarNavigate}
                                                onSelectSlot={handleSelectSlot}
                                                onSelectEvent={handleSelectEvent}
                                                eventPropGetter={eventStyleGetter}
                                                selected={selectedAppointment || selectedAppointmentRef.current}
                                                components={{
                                                    toolbar: AppointmentCalendarToolbar,
                                                    event: CalendarEventContent
                                                }}
                                                messages={{
                                                    next: "Próximo",
                                                    previous: "Anterior",
                                                    today: "Hoje",
                                                    month: "Mês",
                                                    week: "Semana",
                                                    day: "Dia",
                                                    agenda: "Agenda",
                                                    date: "Data",
                                                    time: "Hora",
                                                    event: "Evento",
                                                    noEventsInRange: "Não há agendamentos neste período",
                                                    showMore: (total) => `+ ${total} mais`
                                                }}
                                            />
                                        </div>

                                        <aside className="appointment-dashboard-calendar-sidebar">
                                            <div className="appointment-dashboard-calendar-sidebar__header">
                                                <span className="appointment-dashboard-calendar-sidebar__eyebrow">Resumo do dia</span>
                                                <h5>{moment(currentDate).locale(calendarLocale).format('dddd, DD [de] MMMM')}</h5>
                                                <p>
                                                    Use esta coluna para enxergar a fila do dia sem perder a visao ampla do calendario.
                                                </p>
                                            </div>

                                            <div className="appointment-dashboard-calendar-sidebar__stats">
                                                <div>
                                                    <strong>{calendarFocusStats.total}</strong>
                                                    <span>no dia</span>
                                                </div>
                                                <div>
                                                    <strong>{calendarFocusStats.active}</strong>
                                                    <span>ativos</span>
                                                </div>
                                                <div>
                                                    <strong>{calendarFocusStats.completed}</strong>
                                                    <span>concluidos</span>
                                                </div>
                                                <div>
                                                    <strong>{calendarFocusStats.friction}</strong>
                                                    <span>friccao</span>
                                                </div>
                                            </div>

                                            {calendarFocusAppointments.length === 0 ? (
                                                <div className="appointment-dashboard-calendar-sidebar__empty">
                                                    <i className="fas fa-calendar-day"></i>
                                                    <span>Nenhum atendimento nesse dia.</span>
                                                </div>
                                            ) : (
                                                <div className="appointment-dashboard-calendar-sidebar__list">
                                                    {calendarFocusAppointments.map((event) => (
                                                        <button
                                                            key={event.id}
                                                            type="button"
                                                            className={`appointment-dashboard-calendar-sidebar__item ${selectedAppointment?.id === event.resource?.id ? 'is-selected' : ''}`}
                                                            style={event.resource?.customer?.clubMember ? { borderLeft: '4px solid #dfb244' } : {}}
                                                            onClick={() => handleSelectEvent(event)}
                                                        >
                                                            <div className="appointment-dashboard-calendar-sidebar__item-top">
                                                                <strong>{moment(event.start).format('HH:mm')}</strong>
                                                                <StatusPill status={event.status} subtle />
                                                            </div>
                                                            <div className="appointment-dashboard-calendar-sidebar__item-title">
                                                                {getDisplayName(event.resource?.customer, 'Cliente')}
                                                                {event.resource?.firstVisit && (
                                                                    <i className="fa-solid fa-star appointment-first-visit-icon ms-1"
                                                                        title="Primeiro atendimento deste cliente" aria-label="Primeiro atendimento" />
                                                                )}
                                                                {event.resource?.customerBirthday && (
                                                                    <i className="fa-solid fa-cake-candles appointment-customer-birthday-icon ms-1"
                                                                        title="Aniversário do cliente" aria-label="Aniversário do cliente" />
                                                                )}
                                                                {event.resource?.customer?.clubMember && (
                                                                    <ClubCrownIcon />
                                                                )}
                                                                {event.resource?.customer?.clubLate && (
                                                                    <ClubLateIcon className="club-late-icon ms-1" />
                                                                )}
                                                            </div>
                                                            <div className="appointment-dashboard-calendar-sidebar__item-meta">
                                                                <span>{getDisplayName(event.resource?.employee, 'Colaborador')}</span>
                                                                <span>{getServicesLabel(event.resource, 'Servico')}</span>
                                                                {event.resource?.customer?.branch?.name && (
                                                                    <span title={`Unidade de origem: ${event.resource.customer.branch.name}`}>
                                                                        <i className="fa-solid fa-location-dot text-muted me-1"></i>
                                                                        Origem: {event.resource.customer.branch.name}
                                                                    </span>
                                                                )}
                                                                {event.resource?.customer?.clubPlanName && (
                                                                    <span className="text-warning fw-bold" title={`Plano do Clube: ${event.resource.customer.clubPlanName}`}>
                                                                        <i className="fa-solid fa-gem me-1"></i>
                                                                        Plano: {event.resource.customer.clubPlanName}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </aside>
                                    </div>
                                </div>
                            ) : (
                                <div className="appointment-dashboard-collaborator-content">
                                    {showLoading ? (
                                        <div className="appointment-dashboard-empty-state">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Carregando...</span>
                                            </div>
                                        </div>
                                    ) : collaboratorData.length === 0 ? (
                                        <div className="appointment-dashboard-empty-state">
                                            <i className={isCollaboratorDayClosed ? 'fas fa-store-slash' : 'fas fa-calendar-times'}></i>
                                            <p className="mt-3 mb-0">
                                                {isCollaboratorDayClosed
                                                    ? `Filial fechada neste dia${collaboratorClosedReason ? ` (${collaboratorClosedReason})` : ''}`
                                                    : 'Nenhum agendamento para este dia'}
                                            </p>
                                            {!isCollaboratorDayClosed && collaboratorSpecialHours.length > 0 && (
                                                <p className="mt-2 mb-0 small">
                                                    Horário especial: {collaboratorSpecialHoursText}
                                                </p>
                                            )}
                                            {
                                                // Em dia fechado o botão sairia: o servidor recusa o
                                                // agendamento e nenhum horário livre é oferecido.
                                                isCollaboratorDayClosed ?
                                                    <></>
                                                    :
                                                    <button
                                                        type="button"
                                                        className="collab-new-apt-empty-btn mt-3"
                                                        onClick={() => openSlotPlanner(collaboratorDate)}
                                                    >
                                                        <i className="fas fa-plus"></i>
                                                        Novo agendamento
                                                    </button>
                                            }
                                        </div>
                                    ) : (
                                        <>
                                            {
                                                // Um aviso só, acima da grade. Marcar coluna por
                                                // coluna repetia a mesma informação uma vez por
                                                // colaborador e poluía o quadro sem acrescentar nada.
                                                isCollaboratorDayClosed ?
                                                    <div className="collab-closed-banner">
                                                        <i className="fas fa-store-slash"></i>
                                                        <div>
                                                            <strong>
                                                                Filial fechada neste dia{collaboratorClosedReason ? ` (${collaboratorClosedReason})` : ''}
                                                            </strong>
                                                            <span>
                                                                Não é possível agendar. Os atendimentos abaixo já estavam
                                                                registrados nesta data: remarque ou avise os clientes.
                                                            </span>
                                                        </div>
                                                    </div>
                                                    : collaboratorSpecialHours.length > 0 ?
                                                    <div className="collab-closed-banner">
                                                        <i className="fas fa-clock"></i>
                                                        <div>
                                                            <strong>Horário especial neste dia</strong>
                                                            <span>{collaboratorSpecialHoursText}</span>
                                                        </div>
                                                    </div>
                                                    :
                                                    <></>
                                            }
                                            <div
                                                className="collab-board-shell"
                                                ref={collaboratorBoardShellRef}
                                                onMouseDown={handleBoardPanStart}
                                                onScroll={rememberBoardScroll}
                                            >
                                                <div className="collab-board">
                                                    <div className="collab-board__header">
                                                        <div className="collab-board__timeline-spacer" style={{ height: `${COLLABORATOR_BOARD_HEADER_HEIGHT}px` }}></div>
                                                        <div className="collab-grid collab-grid--header" style={{ gridTemplateColumns: collaboratorColumnsTemplate }}>
                                                            {collaboratorData.map(empData => (
                                                                <div key={`header-${empData.employee.id}`} className={`collab-column collab-column--header ${empData.isDayOff || empData.isOnVacation ? 'is-day-off' : ''}`}>
                                                                    <div className="collab-col-header">
                                                                        <div className="collab-col-header__profile">
                                                                            <EmployeeAvatar employee={empData.employee} companyUuid={companyUuid} />
                                                                            <div className="collab-col-header__identity">
                                                                                <div className="collab-emp-name">{getDisplayName(empData.employee)}</div>
                                                                                {empData.isOnVacation && (
                                                                                    <span className="collab-col-header__vacation">
                                                                                        <i className="fa-solid fa-umbrella-beach" aria-hidden="true"></i>
                                                                                        De férias
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div className="collab-board__body">
                                                        <div className="collab-board__timeline">
                                                            <div className="collab-board__timeline-track" style={{ height: `${collaboratorBoardConfig.totalHeight}px` }}>
                                                                {collaboratorCurrentTimeTop != null && (
                                                                    <div className="collab-board__now-line" style={{ top: `${collaboratorCurrentTimeTop}px` }}>
                                                                        <span>Agora</span>
                                                                    </div>
                                                                )}
                                                                {collaboratorBoardConfig.slots.map((slotMinutes) => {
                                                                    const isHourMark = slotMinutes % 60 === 0;
                                                                    const slotLabel = slotMinutes === collaboratorBoardConfig.endMinutes
                                                                        ? '23:59'
                                                                        : moment(collaboratorDate).startOf('day').add(slotMinutes, 'minutes').format('HH:mm');
                                                                    return (
                                                                        <div
                                                                            key={`time-${slotMinutes}`}
                                                                            className={`collab-board__time-slot ${isHourMark ? 'is-hour' : 'is-half'}`}
                                                                            style={{ top: `${((slotMinutes - collaboratorBoardConfig.startMinutes) / COLLABORATOR_BOARD_INTERVAL_MINUTES) * COLLABORATOR_BOARD_SLOT_HEIGHT}px` }}
                                                                        >
                                                                            <span>{slotLabel}</span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                        <div className="collab-grid collab-grid--body" style={{ gridTemplateColumns: collaboratorColumnsTemplate }}>
                                                            {collaboratorData.map(empData => (
                                                                <div key={empData.employee.id} className={`collab-column ${empData.isDayOff || empData.isOnVacation ? 'is-day-off' : ''}`}>
                                                                    <div
                                                                        className="collab-col-body"
                                                                        data-collab-column={empData.employee.id}
                                                                        style={{ height: `${collaboratorBoardConfig.totalHeight}px` }}
                                                                        onMouseDown={(e) => { colBodyMouseDownTargetRef.current = e.target; }}
                                                                        onClick={(e) => handleCollaboratorBoardSlotClick(e, empData)}
                                                                    >
                                                                        {collaboratorCurrentTimeTop != null && (
                                                                            <div className="collab-col-body__now-line" style={{ top: `${collaboratorCurrentTimeTop}px` }} />
                                                                        )}
                                                                        {collaboratorBoardConfig.slots.map((slotMinutes) => (
                                                                            <div
                                                                                key={`line-${empData.employee.id}-${slotMinutes}`}
                                                                                className={`collab-col-body__slot ${slotMinutes % 60 === 0 ? 'is-hour' : 'is-half'}`}
                                                                                style={{ top: `${((slotMinutes - collaboratorBoardConfig.startMinutes) / COLLABORATOR_BOARD_INTERVAL_MINUTES) * COLLABORATOR_BOARD_SLOT_HEIGHT}px` }}
                                                                            />
                                                                        ))}
                                                                        {/* Férias têm prioridade sobre a folga: cobrem o dia inteiro
                                                                            e o motivo mais útil para a recepção é a data da volta. */}
                                                                        {empData.isOnVacation && (
                                                                            <div
                                                                                className="collab-col-body__blocked-period collab-col-body__blocked-period--day-off collab-col-body__blocked-period--vacation"
                                                                                style={dayOffBlockStyle}
                                                                                title={vacationMessage(empData.employee, getDisplayName(empData.employee, 'O colaborador'))}
                                                                                onMouseDown={(event) => event.stopPropagation()}
                                                                                onClick={(event) => event.stopPropagation()}
                                                                            >
                                                                                <strong>
                                                                                    <i className="fa-solid fa-umbrella-beach"></i>
                                                                                    Férias
                                                                                </strong>
                                                                                <span>Agenda bloqueada</span>
                                                                                <small>Volta em {vacationReturnLabel(empData.employee)}</small>
                                                                            </div>
                                                                        )}
                                                                        {empData.isDayOff && !empData.isOnVacation && (
                                                                            <div
                                                                                className="collab-col-body__blocked-period collab-col-body__blocked-period--day-off"
                                                                                style={dayOffBlockStyle}
                                                                                title="O colaborador não atende neste dia da semana"
                                                                                onMouseDown={(event) => event.stopPropagation()}
                                                                                onClick={(event) => event.stopPropagation()}
                                                                            >
                                                                                <strong>
                                                                                    <i className="fa-solid fa-lock"></i>
                                                                                    Bloqueado
                                                                                </strong>
                                                                                <span>Não atende neste dia</span>
                                                                            </div>
                                                                        )}
                                                                        {(() => {
                                                                            const blockedPeriod = getEmployeeBlockedPeriod(empData.employee);
                                                                            if (!blockedPeriod) return null;

                                                                            return (
                                                                                <div
                                                                                    className="collab-col-body__blocked-period"
                                                                                    style={blockedPeriod.style}
                                                                                    title={`Horário de almoço: ${blockedPeriod.label}`}
                                                                                    onMouseDown={(event) => event.stopPropagation()}
                                                                                    onClick={(event) => event.stopPropagation()}
                                                                                >
                                                                                    <strong>
                                                                                        <i className="fa-solid fa-lock"></i>
                                                                                        Bloqueado
                                                                                    </strong>
                                                                                    <span>Horário de almoço</span>
                                                                                    <small>{blockedPeriod.label}</small>
                                                                                    <div className="collab-col-body__blocked-period-actions">
                                                                                        <button
                                                                                            type="button"
                                                                                            className="collab-col-body__blocked-period-action is-encaixe"
                                                                                            title="Criar encaixe durante o almoço"
                                                                                            aria-label={`Criar encaixe durante o almoço de ${empData.employee.name || getDisplayName(empData.employee)} das ${blockedPeriod.label}`}
                                                                                            onClick={(event) => {
                                                                                                event.stopPropagation();
                                                                                                handleEncaixeBlockRequest({
                                                                                                    branchId: selectedBranch?.id || empData.employee.branchId || empData.employee.branch?.id,
                                                                                                    startDate: `${moment(collaboratorDate).format('YYYY-MM-DD')}T${String(empData.employee.lunchStartTime).slice(0, 8)}`
                                                                                                }, empData.employee);
                                                                                            }}
                                                                                        >
                                                                                            <i className="fa-solid fa-arrows-to-dot" aria-hidden="true"></i>
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })()}
                                        {appointmentBlocks
                                            .filter((block) => String(block.employeeId) === String(empData.employee.id))
                                            .map((block) => (
                                                                                <div
                                                                                    key={`block-${block.id}`}
                                                                                    className="collab-col-body__blocked-period collab-col-body__blocked-period--registered"
                                                                                    style={getRegisteredBlockStyle(block)}
                                                                                    role="group"
                                                                                    aria-label={`Bloqueio: ${block.reason}, ${moment(block.startDate).format('HH:mm')} até ${moment(block.endDate).format('HH:mm')}`}
                                                                                    onMouseDown={(event) => event.stopPropagation()}
                                                                                >
                                                                                    <strong><i className="fa-solid fa-lock"></i>Bloqueado</strong>
                                                                                    <span>{block.reason}</span>
                                                                                    <small>{moment(block.startDate).format('HH:mm')} - {moment(block.endDate).format('HH:mm')}</small>
                                                                                    <div className="collab-col-body__blocked-period-actions">
                                                                                        <button
                                                                                            type="button"
                                                                                            className="collab-col-body__blocked-period-action is-encaixe"
                                                                                            title="Criar encaixe durante este bloqueio"
                                                                                            aria-label={`Criar encaixe durante o bloqueio das ${moment(block.startDate).format('HH:mm')} às ${moment(block.endDate).format('HH:mm')}`}
                                                                                            onClick={(event) => {
                                                                                                event.stopPropagation();
                                                                                                handleEncaixeBlockRequest(block, empData.employee);
                                                                                            }}
                                                                                        >
                                                                                            <i className="fa-solid fa-arrows-to-dot" aria-hidden="true"></i>
                                                                                        </button>
                                                                                        <button
                                                                                            type="button"
                                                                                            className="collab-col-body__blocked-period-action"
                                                                                            title="Cancelar bloqueio"
                                                                                            aria-label={`Cancelar bloqueio ${block.reason}`}
                                                                                            onClick={(event) => {
                                                                                                event.stopPropagation();
                                                                                                cancelAppointmentBlock(block);
                                                                                            }}
                                                                                        >
                                                                                            <i className="fa-solid fa-xmark" aria-hidden="true"></i>
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        {empData.appointments.map((evt) => renderCollaboratorAppointment(evt, empData.employee.id))}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <AppointmentDetailsModal
                show={Boolean(selectedAppointment)}
                appointment={selectedAppointment}
                onHide={() => {
                    // O ref precisa cair junto com o estado. Ele sobrevivia ao fechamento, e
                    // o refresh que roda depois de qualquer salvamento reabre o que estiver
                    // nele: arrastar um cartão fazia o detalhe de um atendimento fechado
                    // minutos antes voltar sozinho à tela.
                    selectedAppointmentRef.current = null;
                    setSelectedAppointment(null);
                }}
                onAppointmentUpdated={(updatedAppointment) => {
                    if (updatedAppointment) {
                        selectedAppointmentRef.current = updatedAppointment;
                        setSelectedAppointment(updatedAppointment);
                    }
                    refreshAppointments();
                }}
                onReschedule={handleRescheduleRequest}
                onEdit={handleEditRequest}
                onAddServices={handleAddServicesRequest}
                onEncaixe={handleEncaixeRequest}
            />

            {slotPlannerOpen && (
                <>
                    <div className="appointment-slot-planner-backdrop" onClick={closeSlotPlanner}></div>
                    <aside className="appointment-slot-planner" aria-label="Agendamento rápido">
                        <div className="appointment-slot-planner__header">
                            <div className="appointment-slot-planner__title-wrap">
                                <span className="appointment-slot-planner__eyebrow">
                                    {editingAppointment
                                        ? (servicesOnlyAppointment ? 'Serviços do atendimento' : 'Editar Atendimento')
                                        : encaixeMode
                                            ? 'Encaixe'
                                            : reschedulingAppointment ? 'Reagendar Atendimento' : 'Agendamento rápido'}
                                </span>
                                <strong>
                                    {slotPlannerDate
                                        ? moment(slotPlannerDate).locale(calendarLocale).format('dddd, DD [de] MMMM')
                                        : 'Selecione um dia'}
                                </strong>
                            </div>
                            <button
                                type="button"
                                className="appointment-slot-planner__close"
                                onClick={closeSlotPlanner}
                                aria-label="Fechar painel"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        <div className="appointment-slot-planner__body">
                            {!reschedulingAppointment && !editingAppointment && !encaixeMode && (
                                <Button variant="secondary" className="w-100 mb-3" onClick={openBlockModal}>
                                    <i className="fa-solid fa-lock me-2"></i>
                                    Bloquear este horário
                                </Button>
                            )}
                            <Alert variant={encaixeMode ? 'warning' : 'info'} className="appointment-slot-planner__hint">
                                {editingAppointment
                                    ? (servicesOnlyAppointment
                                        ? 'Adicione ou remova serviços. O horário e a duração serão preservados.'
                                        : 'O horário é mantido como está. Para mudar data ou hora, use Reagendar.')
                                    : encaixeMode
                                        ? (encaixeReference
                                            ? `Encaixe sobre o atendimento de ${getDisplayName(encaixeReference.customer, 'cliente')} às ${moment(encaixeReference.scheduledDate).format('HH:mm')}. A sobreposição é intencional e o conflito não será bloqueado.`
                                            : 'Escolha o cliente, profissional, serviço e horário. O encaixe pode ocupar um horário que já esteja preenchido.')
                                        : 'Os horários exibidos consideram a duração do serviço escolhido para o profissional selecionado.'}
                            </Alert>

                            <div className="appointment-slot-planner__filters">
                                {!servicesOnlyAppointment && (
                                    <>
                                <div className="appointment-slot-planner__field">
                                    <label className="form-label">Data</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={slotPlannerDate ? moment(slotPlannerDate).format('YYYY-MM-DD') : ''}
                                        onChange={(e) => {
                                            if (e.target.value) {
                                                setSlotPlannerDate(moment(e.target.value, 'YYYY-MM-DD').toDate());
                                            }
                                        }}
                                        // Na edição a data é informativa: mudar horário é Reagendar.
                                        disabled={slotPlannerSubmitting || slotPlannerLoading || Boolean(editingAppointment)}
                                    />
                                </div>

                                {!reschedulingAppointment && !encaixeMode && (
                                    <div className="appointment-slot-planner__field">
                                        <Form.Check
                                            type="switch"
                                            id="quick-specific-time"
                                            label="Informar um horário específico"
                                            checked={useSpecificQuickTime}
                                            onChange={(event) => setUseSpecificQuickTime(event.target.checked)}
                                            disabled={slotPlannerSubmitting || slotPlannerLoading}
                                        />
                                        {useSpecificQuickTime && (
                                            <Row className="g-2 mt-1">
                                                <Col xs={6}>
                                                    <label className="form-label mb-1">Horário de início</label>
                                                    <Form.Control
                                                        type="time"
                                                        value={specificQuickStartTime}
                                                        onChange={(event) => setSpecificQuickStartTime(event.target.value)}
                                                        disabled={slotPlannerSubmitting}
                                                    />
                                                </Col>
                                                <Col xs={6}>
                                                    <label className="form-label mb-1">Horário de fim</label>
                                                    <Form.Control
                                                        type="time"
                                                        value={specificQuickEndTime}
                                                        onChange={(event) => setSpecificQuickEndTime(event.target.value)}
                                                        disabled={slotPlannerSubmitting}
                                                    />
                                                </Col>
                                            </Row>
                                        )}
                                    </div>
                                )}

                                {/* No encaixe o horário é digitado, não escolhido numa lista: o
                                    horário desejado é justamente um que já está ocupado e por isso
                                    não apareceria entre os livres. */}
                                {encaixeMode && (
                                    <div className="appointment-slot-planner__field">
                                        <label className="form-label">Horário do encaixe</label>
                                        <input
                                            type="time"
                                            className="form-control"
                                            value={encaixeTime}
                                            onChange={(event) => setEncaixeTime(event.target.value)}
                                            disabled={slotPlannerSubmitting}
                                        />
                                    </div>
                                )}

                                <div className="appointment-slot-planner__field">
                                    {/* Cliente novo na porta: cadastrar sem sair do painel evita
                                        perder o que já foi preenchido aqui. */}
                                    <div className="appointment-slot-planner__field-header">
                                        <label className="form-label mb-0">Cliente</label>
                                        <button
                                            type="button"
                                            className="appointment-slot-planner__inline-action"
                                            onClick={openQuickCustomerFromPlanner}
                                            disabled={slotPlannerSubmitting}
                                        >
                                            <i className="fas fa-plus"></i>
                                            Novo cliente
                                        </button>
                                    </div>
                                    <CustomerSelect
                                        value={plannerCustomer}
                                        onChange={(item) => setPlannerCustomer(item)}
                                        isDisabled={slotPlannerSubmitting}
                                        crossBranch
                                    />
                                    {plannerCustomerSubscription?.plan && (
                                        <div className="appointment-slot-planner__customer-plan">
                                            <PlanDetails
                                                showDetailsButton={false}
                                                planStatus={plannerCustomerSubscription}
                                                plan={{
                                                    ...plannerCustomerSubscription.plan,
                                                    label: plannerCustomerSubscription.plan.name
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="appointment-slot-planner__field">
                                    <label className="form-label">Filial</label>
                                    <CustomSelect
                                        value={plannerBranch}
                                        onChange={(option) => setPlannerBranch(option)}
                                        options={plannerBranchOptions}
                                        isDisabled={slotPlannerSubmitting || slotPlannerLoading}
                                        isClearable
                                        placeholder="Selecione"
                                    />
                                </div>

                                <div className="appointment-slot-planner__field">
                                    <label className="form-label">Colaborador</label>
                                    <CustomSelect
                                        value={plannerEmployee}
                                        onChange={(option) => setPlannerEmployee(option)}
                                        options={plannerEmployeeOptions}
                                        isDisabled={slotPlannerSubmitting || slotPlannerLoading || !plannerBranch}
                                        isClearable
                                        placeholder="Selecione"
                                    />
                                </div>
                                    </>
                                )}

                                <div className="appointment-slot-planner__field">
                                    <label className="form-label">{plannerServices.length > 1 ? 'Serviços' : 'Serviço'}</label>
                                    <CustomSelect
                                        value={plannerServices}
                                        onChange={(options) => setPlannerServices(options || [])}
                                        options={plannerServiceOptions}
                                        isDisabled={slotPlannerSubmitting || slotPlannerLoading || !plannerBranch}
                                        isMulti
                                        isClearable
                                        placeholder="Selecione um ou mais serviços"
                                    />
                                    {plannerServices.length > 0 && (
                                        <small className="appointment-slot-planner__service-meta">
                                            {plannerTotalDuration} min
                                            {plannerTotalPrice > 0
                                                ? ` • ${plannerHasPriceFrom ? 'a partir de ' : ''}R$ ${formatNumberWithTwoDecimals(plannerTotalPrice)}`
                                                : ''}
                                        </small>
                                    )}
                                </div>

                                {/* Reserva só na criação: edição e reagendamento mexem num agendamento que
                                    já existe, e a reserva dele se trata pelo detalhe. */}
                                {!editingAppointment && !reschedulingAppointment && !servicesOnlyAppointment && (
                                    <AppointmentDepositDraftSection
                                        draft={plannerDeposit}
                                        onChange={setPlannerDeposit}
                                        serviceOptions={plannerServices}
                                        totalPrice={plannerTotalPrice}
                                        disabled={slotPlannerSubmitting}
                                    />
                                )}

                                {/* Observação na edição e no encaixe: nos dois casos há contexto que
                                    vale registrar. Na criação simples o campo alongaria o caminho de
                                    quem só quer marcar um horário rápido. */}
                                {!servicesOnlyAppointment && (editingAppointment || encaixeMode) && (
                                    <div className="appointment-slot-planner__field">
                                        <label className="form-label">Observação</label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            value={plannerNotes}
                                            onChange={(event) => setPlannerNotes(event.target.value)}
                                            disabled={slotPlannerSubmitting}
                                            placeholder="Alguma informação sobre o atendimento"
                                        />
                                    </div>
                                )}
                            </div>

                            {editingAppointment ? (
                                <div className="appointment-slot-planner__slots">
                                    <div className="appointment-slot-planner__section-head">
                                        <h6 className="mb-0">Horário do atendimento</h6>
                                        <span>
                                            {moment(editingAppointment.scheduledDate).format('DD/MM/YYYY [às] HH:mm')}
                                        </span>
                                    </div>
                                    <Button
                                        variant="primary"
                                        className="w-100"
                                        onClick={handleSaveAppointmentEdit}
                                        disabled={slotPlannerSubmitting}
                                    >
                                        {slotPlannerSubmitting ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Salvando...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-floppy-disk me-2"></i>
                                                {servicesOnlyAppointment ? 'Salvar serviços' : 'Salvar alterações'}
                                            </>
                                        )}
                                    </Button>
                                </div>
                            ) : encaixeMode ? (
                                <div className="appointment-slot-planner__slots">
                                    <Button
                                        variant="warning"
                                        className="w-100"
                                        onClick={handleCreateEncaixe}
                                        disabled={slotPlannerSubmitting}
                                    >
                                        {slotPlannerSubmitting ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Encaixando...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-arrows-to-dot me-2"></i>
                                                Confirmar encaixe
                                            </>
                                        )}
                                    </Button>
                                </div>
                            ) : useSpecificQuickTime ? (
                                <div className="appointment-slot-planner__slots">
                                    <div className="appointment-slot-planner__section-head">
                                        <h6 className="mb-0">Horário informado</h6>
                                    </div>
                                    <Button
                                        variant="primary"
                                        className="w-100"
                                        onClick={() => handleQuickAppointmentCreate({}, specificQuickStartTime, specificQuickEndTime)}
                                        disabled={slotPlannerSubmitting || !specificQuickStartTime || !specificQuickEndTime}
                                    >
                                        {slotPlannerSubmitting ? (
                                            <><Spinner animation="border" size="sm" className="me-2" />Salvando...</>
                                        ) : (
                                            <><i className="fas fa-calendar-check me-2"></i>Confirmar horário</>
                                        )}
                                    </Button>
                                </div>
                            ) : (
                            <div className="appointment-slot-planner__slots">
                                <div className="appointment-slot-planner__section-head">
                                    <h6 className="mb-0">Horários disponíveis</h6>
                                    {slotPlannerDate && (
                                        <span>{moment(slotPlannerDate).format('DD/MM/YYYY')}</span>
                                    )}
                                </div>

                                {slotPlannerLoading ? (
                                    <div className="appointment-slot-planner__empty">
                                        <Spinner animation="border" variant="primary" size="sm" />
                                        <span>Carregando horários...</span>
                                    </div>
                                ) : slotPlannerError ? (
                                    <Alert variant="danger" className="mb-0">
                                        {slotPlannerError}
                                    </Alert>
                                ) : !plannerBranch || !plannerEmployee || plannerServices.length === 0 ? (
                                    <div className="appointment-slot-planner__empty">
                                        <i className="fas fa-filter"></i>
                                        <span>Selecione filial, profissional e serviço para listar os horários.</span>
                                    </div>
                                ) : plannerSlots.length === 0 ? (
                                    <div className="appointment-slot-planner__empty">
                                        <i className="fas fa-calendar-times"></i>
                                        <span>
                                            {isPlannerEmployeeOnVacation
                                                ? vacationMessage(plannerEmployee.employee, plannerEmployee.label)
                                                : isPlannerEmployeeDayOff
                                                    ? `${plannerEmployee.label} não atende neste dia da semana.`
                                                    : 'Nenhum horário livre encontrado para esse dia.'}
                                        </span>
                                    </div>
                                ) : (
                                    <div className="appointment-slot-planner__slot-grid">
                                        {plannerSlots.map((slot) => (
                                            <button
                                                key={`${slot.startTime || slot.time}`}
                                                type="button"
                                                className={`appointment-slot-planner__slot ${slot.lunchBreak ? 'appointment-slot-planner__slot--blocked' : ''}`}
                                                disabled={slotPlannerSubmitting || slot.lunchBreak}
                                                onClick={() => !slot.lunchBreak && handleQuickAppointmentCreate(slot)}
                                            >
                                                <strong>{formatSlotTime(slot)}</strong>
                                                <span>
                                                    {slot.lunchBreak
                                                        ? <><i className="fa-solid fa-lock appointment-slot-planner__lunch-icon"></i> Horário de almoço</>
                                                        : <>Clique para agendar</>}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            )}
                        </div>
                    </aside>
                </>
            )}
            {/* Fica fora do painel, que é escondido enquanto o cadastro está aberto (ver
                openQuickCustomerFromPlanner). O cliente criado já volta selecionado. */}
            <CustomerQuickCreateModal
                show={quickCustomerOpen}
                onHide={() => setQuickCustomerOpen(false)}
                onCreated={(option) => setPlannerCustomer(option)}
                onExited={restorePlannerAfterQuickCustomer}
                defaultBranch={plannerBranch}
            />
            <Modal
                show={showBlockModal}
                onHide={() => !blockSubmitting && setShowBlockModal(false)}
                centered
                className="appointment-block-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>{blockDraft.reason === 'Folga' ? 'Registrar folga' : 'Bloquear agenda'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row className="g-3 mb-3">
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label>Filial</Form.Label>
                                {/* Era um campo de texto apenas leitura, alimentado pela filial do
                                    topo. Quando o operador está com "Todas as filiais" e abre o
                                    bloqueio pelo quadro, não havia filial nenhuma para herdar e o
                                    campo ficava vazio, sem como preencher: o registro travava na
                                    validação. Com o select padrão a filial pode ser escolhida. */}
                                <BranchSelect
                                    name="branch"
                                    value={blockBranchValue}
                                    onChange={(option) => setBlockDraft((current) => ({
                                        ...current,
                                        branchId: option?.value ?? null,
                                        branchLabel: option?.label ?? ''
                                    }))}
                                    isDisabled={blockSubmitting}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label>Colaborador</Form.Label>
                                {/* Vem depois da filial porque a lista depende dela: só quem é
                                    da unidade escolhida pode ter a agenda bloqueada ali. */}
                                <CustomSelect
                                    value={blockEmployeeValue}
                                    onChange={(option) => setBlockDraft((current) => ({
                                        ...current,
                                        employeeId: option?.value ?? null,
                                        employeeLabel: option?.label ?? ''
                                    }))}
                                    options={blockEmployeeOptions}
                                    isClearable
                                    isLoading={blockEmployeesLoading}
                                    isDisabled={blockSubmitting || !blockDraft.branchId}
                                    placeholder={blockDraft.branchId ? 'Selecione' : 'Selecione a filial primeiro'}
                                    noOptionsMessage={() => 'Nenhum colaborador nesta filial'}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Form.Group className="mb-3">
                        <Form.Label>{blockDraft.reason === 'Folga' ? 'Primeiro dia' : 'Início'}</Form.Label>
                        <Form.Control
                            type={blockDraft.reason === 'Folga' ? 'date' : 'datetime-local'}
                            value={blockDraft.reason === 'Folga'
                                ? moment(blockDraft.startDate).format('YYYY-MM-DD')
                                : blockDraft.startDate}
                            onChange={(event) => setBlockDraft((current) => ({
                                ...current,
                                startDate: blockDraft.reason === 'Folga'
                                    ? moment(event.target.value).startOf('day').format('YYYY-MM-DDTHH:mm')
                                    : event.target.value
                            }))} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{blockDraft.reason === 'Folga' ? 'Último dia' : 'Fim'}</Form.Label>
                        <Form.Control
                            type={blockDraft.reason === 'Folga' ? 'date' : 'datetime-local'}
                            value={blockDraft.reason === 'Folga'
                                ? moment(blockDraft.endDate).subtract(1, 'day').format('YYYY-MM-DD')
                                : blockDraft.endDate}
                            onChange={(event) => setBlockDraft((current) => ({
                                ...current,
                                endDate: blockDraft.reason === 'Folga'
                                    ? moment(event.target.value).add(1, 'day').startOf('day').format('YYYY-MM-DDTHH:mm')
                                    : event.target.value
                            }))} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Motivo</Form.Label>
                        <CustomSelect
                            value={APPOINTMENT_BLOCK_REASON_OPTIONS.find(
                                (option) => option.value === blockDraft.reason
                            ) || null}
                            onChange={(option) => setBlockDraft((current) => ({
                                ...current,
                                reason: option?.value ?? '',
                                ...(option?.value === 'Folga' ? {
                                    startDate: moment(current.startDate).startOf('day').format('YYYY-MM-DDTHH:mm'),
                                    // Folga inclui o último dia escolhido; o fim salvo é exclusivo.
                                    endDate: moment(current.startDate).startOf('day').add(1, 'day').format('YYYY-MM-DDTHH:mm')
                                } : {})
                            }))}
                            options={APPOINTMENT_BLOCK_REASON_OPTIONS}
                            isClearable
                            placeholder="Selecione"
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Observação</Form.Label>
                        <Form.Control as="textarea" rows={3} maxLength={500} value={blockDraft.notes}
                            onChange={(event) => setBlockDraft((current) => ({ ...current, notes: event.target.value }))} />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setShowBlockModal(false)} disabled={blockSubmitting}>Cancelar</Button>
                    <Button variant={blockDraft.reason === 'Folga' ? 'primary' : 'secondary'} onClick={createAppointmentBlock} disabled={blockSubmitting || !blockDraft.startDate || !blockDraft.endDate}>
                        {blockSubmitting ? 'Salvando...' : blockDraft.reason === 'Folga' ? 'Salvar folga' : 'Registrar bloqueio'}
                    </Button>
                </Modal.Footer>
            </Modal>
            </section>
        </>
    );
}

export default AppointmentDashboard;
